import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { getSkinSrc } from "./SeasonalSkins";
import { useAppConfig } from "../ConfigContext";
import LoadingSpinner from "./LoadingSpinner";
import MyTapisPage from "./MyTapisPage";
import EmailVerificationModal from "./EmailVerificationModal";
import {
  useTonAddress,
  useTonConnectUI,
  useTonWallet,
} from "@tonconnect/ui-react";
import {
  getNameStyleCSS,
  getUserBannerStyle,
  getUserNameCosmetics,
} from "./GameNameStyles";
import "../index.css";

const API_BASE = process.env.REACT_APP_API_URL;
const PREVIEW_LIMIT = 3;
const FALLBACK_DECK_SIZE = 3;
const OVERALL_DECK_KEY = "overall";

function normalizeReferralItemType(type) {
  const value = String(type ?? "")
    .trim()
    .toLowerCase();

  if (
    [
      "cardpieces",
      "cardpiece",
      "card_pieces",
      "card-pieces",
    ].includes(value)
  ) {
    return "cardPieces";
  }

  if (
    [
      "spin",
      "spins",
      "fortune_spin",
      "fortune_spins",
      "fortune-wheel",
      "fortune_wheel",
    ].includes(value)
  ) {
    return "spins";
  }

  if (
    [
      "seasonalaccessory",
      "seasonal_accessory",
      "seasonal-accessory",
      "skin",
      "skins",
    ].includes(value)
  ) {
    return "seasonal_accessory";
  }

  if (["booster", "boosters"].includes(value)) {
    return "booster";
  }

  if (["ability", "abilities"].includes(value)) {
    return "ability";
  }

  if (["rune", "runes"].includes(value)) {
    return "runes";
  }

  if (["spark", "sparks"].includes(value)) {
    return "sparks";
  }

  if (["card", "cards"].includes(value)) {
    return "card";
  }

  if (
    [
      "name",
      "cosmetic",
      "name_cosmetic",
    ].includes(value)
  ) {
    return "name";
  }

  if (value === "tapi") {
    return "tapi";
  }

  if (
    [
      "evolve",
      "evolvement",
    ].includes(value)
  ) {
    return "evolve";
  }

  return value;
}

function normalizeReferralRarity(rarity) {
  const value = String(rarity || "")
    .trim()
    .toLowerCase();

  if (value === "legendary") return "legendary";
  if (value === "epic") return "epic";
  if (value === "rare") return "rare";

  return "common";
}

function referralRarityIcon(rarity) {
  switch (normalizeReferralRarity(rarity)) {
    case "legendary":
      return "/legendary.png";

    case "epic":
      return "/epic.png";

    case "rare":
      return "/rare.png";

    case "common":
    default:
      return "/common.png";
  }
}

function getReferralCardRarity(milestone, item) {
  const directRarity =
    milestone?.cardRarity ??
    milestone?.card_rarity ??
    milestone?.rarity ??
    item?.card_rarity ??
    item?.cardRarity ??
    item?.rarity ??
    item?.card?.rarity;

  if (directRarity) {
    return directRarity;
  }

  const itemName = String(
    item?.display_name ??
    item?.displayName ??
    item?.name ??
    milestone?.itemName ??
    milestone?.label ??
    ""
  ).toLowerCase();

  if (itemName.includes("legendary")) return "legendary";
  if (itemName.includes("epic")) return "epic";
  if (itemName.includes("rare")) return "rare";
  if (itemName.includes("common")) return "common";

  return "common";
}

function getReferralMilestoneIcon(milestone, item) {
  const itemType = normalizeReferralItemType(
    milestone?.itemType ??
    milestone?.item_type ??
    milestone?.type ??
    item?.type
  );

  switch (itemType) {
    case "runes":
      return "/runes.png";

    case "sparks":
      return "/sparks.png";

    case "booster":
      return "/energy.png";

    case "seasonal_accessory":
      return "/fragment.png";

    case "shield":
      return "/shield.png";

    case "ability":
      return "/fightlog.png";

    case "gamepass":
      return "/passicon.png";

    case "cardPieces":
      return "/pieces.png";

    case "spins":
      return "/fortuneicon.png";

    case "tapi":
      return "/icon_tapi.png";

    case "evolve":
      return "/evolveicon.png";

    case "card":
      return referralRarityIcon(
        getReferralCardRarity(milestone, item)
      );

    case "name": {
      const nameCategory = String(
        item?.name_category ??
        item?.nameCategory ??
        milestone?.nameCategory ??
        milestone?.name_category ??
        ""
      )
        .trim()
        .toLowerCase();

      const cosmeticValue = String(
        item?.name_value ??
        item?.nameValue ??
        item?.image ??
        item?.icon ??
        ""
      ).trim();

      if (cosmeticValue) {
        if (
          cosmeticValue.startsWith("/") ||
          cosmeticValue.startsWith("http://") ||
          cosmeticValue.startsWith("https://")
        ) {
          return cosmeticValue;
        }

        return `/name-cosmetics/${cosmeticValue}`;
      }

      if (
        nameCategory === "banners" ||
        nameCategory === "banner"
      ) {
        return "/banner_icon.png";
      }

      if (
        nameCategory === "emogy" ||
        nameCategory === "emoji" ||
        nameCategory === "emojy"
      ) {
        return "/emogy_icon.png";
      }

      return "/style_icon.png";
    }

    default:
      return (
        milestone?.icon ??
        milestone?.itemIcon ??
        milestone?.imageUrl ??
        milestone?.image_url ??
        item?.imageUrl ??
        item?.image_url ??
        item?.image ??
        item?.icon ??
        "/pack.png"
      );
  }
}

function renderReferralSkinPreview(card, seasonName, size = 76) {
  return (
    <div
      style={{
        position: "relative",
        width: size,
        height: size,
        overflow: "visible",
        background: "transparent",
      }}
    >
      {Object.entries(card?.character || {})
        .sort(([a], [b]) => {
          const order = [
            "background",
            "body",
            "pants",
            "shirt",
            "boots",
            "neck",
            "head",
            "face",
            "eyes",
            "mouth",
            "weapons",
          ];

          const indexA = order.includes(a)
            ? order.indexOf(a)
            : order.length;

          const indexB = order.includes(b)
            ? order.indexOf(b)
            : order.length;

          return indexA - indexB;
        })
        .map(([part, data]) => {
          if (part === "background") return null;
          if (!data || typeof data !== "object") return null;

          const defaultSrc =
            `/assets/${part}/${data.side}/${data.rarity}/${data.variant}_thumb.png`;

          const src = getSkinSrc(
            seasonName,
            part,
            defaultSrc
          );

          return (
            <img
              key={part}
              src={src}
              alt=""
              draggable={false}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                imageRendering: "pixelated",
                pointerEvents: "none",
                userSelect: "none",
              }}
              onError={(e) => {
                if (e.currentTarget.src !== defaultSrc) {
                  e.currentTarget.src = defaultSrc;
                }
              }}
            />
          );
        })}
    </div>
  );
}

function svgFallback(name = "?") {
  const initial = (name?.[0] || "?").toUpperCase();
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64">
      <rect width="100%" height="100%" fill="#914422"/>
      <text x="50%" y="50%" dominant-baseline="middle"
            text-anchor="middle" font-size="32"
            fill="#fff" font-family="Arial, sans-serif">${initial}</text>
    </svg>`;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

function formatNum(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString();
}

function safeParseJson(value, fallback) {
  if (value == null) return fallback;
  if (typeof value === "object") return value;

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function parseAbilities(raw) {
  const parsed = safeParseJson(raw, []);
  return Array.isArray(parsed) ? parsed : [];
}

function sanitizeAbilityList(list) {
  return (Array.isArray(list) ? list : [])
    .map((ability) => {
      if (typeof ability === "string") return ability;
      return ability?.name || "";
    })
    .map((ability) => String(ability || "").trim())
    .filter(Boolean);
}

function getCardCharacter(card) {
  const parsed = safeParseJson(card?.character, {});
  return parsed && typeof parsed === "object" ? parsed : {};
}

function MiniStat({ icon, value }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        color: "#fff",
        fontSize: 12,
        fontWeight: 500,
        textShadow: "1px 1px #000",
      }}
    >
      <img
        src={icon}
        alt=""
        style={{
          width: 12,
          height: 12,
          objectFit: "contain",
        }}
      />
      {value}
    </div>
  );
}

function MiniCard({ card, emptyLabel = "EMPTY" }) {
  const character = getCardCharacter(card);
  const abilitiesSafe = sanitizeAbilityList(parseAbilities(card?.abilities));

  if (!card) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          backgroundImage: 'url("/v2/nocard.png"), url("/v2/item.png")',
          backgroundRepeat: "no-repeat, no-repeat",
          backgroundSize: "55%, 100% 100%",
          backgroundPosition: "center center, center center",
          padding: 0,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          cursor: "default",
          overflow: "hidden",
        }}
      >
        <div className="game-button-small yellow-button">{emptyLabel}</div>
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        backgroundImage: 'url("/v2/item.png")',
        padding: 3,
        backgroundRepeat: "repeat",
        backgroundSize: "100% 100%",
        backgroundPosition: "center center",
        position: "relative",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        cursor: "default",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: "100%",
          height: "calc(100% - 24px)",
          borderRadius: 4,
          cursor: "default",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 6,
            right: 5,
            zIndex: 30,
            display: "flex",
            flexDirection: "column",
            gap: 3,
            pointerEvents: "none",
          }}
        >
          {abilitiesSafe.map((ability, idx) => {
            const abilityKey = String(ability || "").toLowerCase();

            return (
              <div
                key={`${ability}-${idx}`}
                style={{
                  width: 15,
                  height: 15,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                title={ability}
              >
                <img
                  src={`/a_${abilityKey}.png`}
                  alt={ability}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                  style={{
                    width: 15,
                    height: 15,
                    objectFit: "contain",
                    pointerEvents: "none",
                  }}
                />
              </div>
            );
          })}
        </div>

        <div
          style={{
            position: "absolute",
            top: 6,
            left: 5,
            width: 28,
            height: 28,
            backgroundImage: "url('/v2/position.png')",
            backgroundSize: "100% 100%",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            color: "#fff",
            fontSize: 15,
            lineHeight: "28px",
            textAlign: "center",
            zIndex: 20,
            fontFamily: '"Fredoka", sans-serif',
            fontWeight: 600,
          }}
        >
          {card.level ?? 1}
        </div>

        <div
          className="char_bg"
          style={{
            position: "relative",
            width: "100%",
            aspectRatio: 1,
            zIndex: 2,
          }}
        >
          <div
            style={{
              zIndex: 20,
              textTransform: "uppercase",
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              color: "#fff",
              height: 18,
              fontSize: 11,
              lineHeight: "21px",
              textAlign: "center",
              fontWeight: 600,
              background: "rgba(77, 44, 36, 0.5)",
            }}
          >
            {card.rarity || "common"}
          </div>

          {Object.entries(character)
            .sort(([a], [b]) => {
              const order = [
                "background",
                "body",
                "boots",
                "pants",
                "shirt",
                "neck",
                "face",
                "eyes",
                "mouth",
                "head",
                "weapons",
              ];

              const indexA = order.includes(a) ? order.indexOf(a) : order.length;
              const indexB = order.includes(b) ? order.indexOf(b) : order.length;

              return indexA - indexB;
            })
            .map(([part, data]) => {
              if (!data || typeof data !== "object") return null;

              const defaultSrc = `/assets/${part}/${data.side}/${data.rarity}/${data.variant}_thumb.png`;

              const src = getSkinSrc(
                card?.seasonal_accessory?.season_name || card?.season_name || null,
                part,
                defaultSrc
              );

              const zIndexMap = {
                background: 1,
                body: 2,
                boots: 3,
                pants: 4,
                shirt: 5,
                neck: 6,
                face: 7,
                eyes: 8,
                mouth: 9,
                head: 10,
                weapons: 11,
              };

              return (
                <img
                  key={part}
                  src={src}
                  alt=""
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    zIndex: zIndexMap[part] ?? 10,
                    pointerEvents: "none",
                  }}
                />
              );
            })}
        </div>
      </div>

      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 4,
          padding: "3px 4px",
          boxSizing: "border-box",
          height: 24,
        }}
      >
        <MiniStat icon="/s_attack.png" value={card.attack ?? 0} />
        <MiniStat icon="/s_defense.png" value={card.defense ?? 0} />
        <MiniStat icon="/s_health.png" value={card.health ?? 0} />
      </div>
    </div>
  );
}

function ProfileStatBox({
  icon,
  fallbackIcon,
  label,
  value,
  onClick,
  title,
}) {
  const clickable = typeof onClick === "function";

  return (
    <div
      onClick={onClick}
role={clickable ? "button" : undefined}
tabIndex={clickable ? 0 : undefined}
title={title}
onKeyDown={(event) => {
  if (!clickable) return;

  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    onClick();
  }
}}
      style={{
        minWidth: 0,
        background: "rgba(80, 35, 16, 0.7)",
        border: "2px solid #5d2a13",
        borderRadius: 6,
        padding: 6,
        textAlign: "center",
        boxSizing: "border-box",
        overflow: "hidden",

        cursor: clickable ? "pointer" : "default",
        transition: clickable
          ? "transform 0.15s ease, filter 0.15s ease"
          : undefined,
      }}
    >
      <div
        className="shadow1"
        style={{
          color: "#fff",
          fontSize: 15,
          lineHeight: "18px",
          fontWeight: 600,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </div>

      <img
        src={icon}
        alt=""
        style={{
          height: 32,
          objectFit: "contain",
          marginTop: 5,
          pointerEvents: "none",
        }}
        onError={(event) => {
          if (fallbackIcon) {
            event.currentTarget.src = fallbackIcon;
          } else {
            event.currentTarget.style.display = "none";
          }
        }}
      />

      <div
        className="shadow1"
        style={{
          color: "#f3e14d",
          fontSize: 16,
          lineHeight: "20px",
          fontWeight: 600,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {formatNum(value)}
      </div>
    </div>
  );
}

function InviteFriendsModal({
  open,
  user,
  setUser,
  onClose,
  onCloseProfile,
  onNavigate,
}) {



 const { referrers } = useAppConfig();

const referralPreviewCard = useMemo(() => {
  const rawSide = String(user?.side || "good")
    .trim()
    .toLowerCase();

  const side =
    rawSide === "bad" || rawSide === "moon"
      ? "moon"
      : "sun";

  const parts = [
    "background",
    "body",
    "boots",
    "eyes",
    "face",
    "head",
    "mouth",
    "neck",
    "pants",
    "shirt",
    "weapons",
  ];

  const character = {};

  parts.forEach((part) => {
    character[part] = {
      side,
      rarity: "Common",
      variant: 1,
    };
  });

  return {
    level: 1,
    rarity: "Common",
    character,
  };
}, [user?.side]);

const runePurchaseCommissionPercent = Math.max(
  0,
  Number(
    referrers?.runePurchaseCommissionPercent ?? 0
  ) || 0
);

const sparkPurchaseCommissionPercent = Math.max(
  0,
  Number(
    referrers?.sparkPurchaseCommissionPercent ?? 0
  ) || 0
);

const qualifiedReferralSparks = Math.max(
  0,
  Number(
    referrers?.qualifiedReferralSparks ?? 0
  ) || 0
);

const qualifiedReferralRunes = Math.max(
  0,
  Number(
    referrers?.qualifiedReferralRunes ?? 0
  ) || 0
);

const referralBattlesRequired = Math.max(
  0,
  Math.floor(
    Number(
      referrers?.qualification?.battlesRequired ?? 0
    ) || 0
  )
);

const requireRunePurchase = Boolean(
  referrers?.qualification?.requireRunePurchase
);

const requireSparkPurchase = Boolean(
  referrers?.qualification?.requireSparkPurchase
);

  const [friends, setFriends] = useState([]);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [referralLink, setReferralLink] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [showReferralsModal, setShowReferralsModal] = useState(false);

              const [referralMilestonesPerView, setReferralMilestonesPerView] =
  useState(3);

useEffect(() => {
  const updateMilestonesPerView = () => {
    setReferralMilestonesPerView(
      window.innerWidth >= 430 ? 4 : 3
    );
  };

  updateMilestonesPerView();

  window.addEventListener(
    "resize",
    updateMilestonesPerView
  );

  return () => {
    window.removeEventListener(
      "resize",
      updateMilestonesPerView
    );
  };
}, []);

  const activeReferrals =
  friends.filter((friend) => {
    return (
      String(
        friend?.status || ""
      )
        .trim()
        .toLowerCase() ===
      "qualified"
    );
  }).length;

const pendingReferrals =
  Math.max(
    0,
    friends.length -
      activeReferrals
  );

  const [pendingRewards, setPendingRewards] = useState({
  sparks: 0,
  runes: 0,
});

const [referralEarnings, setReferralEarnings] = useState({
  sparks: 0,
  runes: 0,
});

const [referralsStreak, setReferralsStreak] = useState({
    enabled: false,
    progress: 0,
    milestones: [],
  });

  const progress = Math.max(
  0,
  Number(
    referralsStreak?.progress ??
    friends.length ??
    0
  ) || 0
);

const apiMilestones = Array.isArray(referralsStreak?.milestones)
  ? referralsStreak.milestones
  : [];

const milestones = apiMilestones
  .map((milestone, index) => {
    const item =
      milestone?.item ||
      milestone?.reward?.item ||
      milestone?.reward ||
      {};

    const count = Math.max(
      1,
      Math.floor(
        Number(
          milestone?.count ??
          milestone?.milestone ??
          milestone?.referrals ??
          milestone?.required ??
          0
        ) || 0
      )
    );

    const label = String(
      milestone?.label ??
      milestone?.itemName ??
      milestone?.name ??
      item?.display_name ??
      item?.displayName ??
      item?.name ??
      item?.label ??
      `Reward ${index + 1}`
    ).trim();

   const icon = getReferralMilestoneIcon(
  milestone,
  item
);

    return {
  ...milestone,
  count,
  label,
  icon,

  item,

  itemType: normalizeReferralItemType(
    milestone?.itemType ??
    milestone?.item_type ??
    milestone?.type ??
    item?.type
  ),

  itemId:
    milestone?.itemId ??
    milestone?.item_id ??
    item?.id ??
    null,
};
  })
  .filter((milestone) => milestone.count > 0)
  .sort((a, b) => a.count - b.count);

const lastMilestoneCount =
  milestones.length > 0
    ? Number(milestones[milestones.length - 1]?.count || 1)
    : 1;

const completedMilestoneCount = milestones.filter(
  (milestone) => progress >= Number(milestone.count || 0)
).length;

const progressPercent =
  milestones.length <= 1
    ? progress >= lastMilestoneCount
      ? 100
      : 0
    : Math.min(
        100,
        Math.max(
          0,
          (completedMilestoneCount / milestones.length) * 100
        )
      );

useEffect(() => {
  if (!open) return;

    let cancelled = false;

    async function loadInviteData() {
      try {
        setFriendsLoading(true);

       const res = await fetch(
  `${process.env.REACT_APP_API_URL}/api/friends`,
  {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  }
);

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(data?.error || "Failed to load referrals");
        }

        if (cancelled) return;

        const code = String(data?.referralCode || "").trim();
        const botUsername =
          process.env.REACT_APP_TELEGRAM_BOT_USERNAME || "taptopiaio_bot";

        setReferralCode(code);
        setReferralLink(
          code
            ? `https://t.me/${botUsername}?startapp=${encodeURIComponent(code)}`
            : ""
        );

        setFriends(Array.isArray(data?.referredFriends) ? data.referredFriends : []);

        setPendingRewards({
  sparks:
    Number(
      data?.pendingSparks ?? 0
    ) || 0,

  runes:
    Number(
      data?.pendingRunes ?? 0
    ) || 0,
});

setReferralEarnings({
  sparks:
    Number(
      data?.lifetimeReferralEarnings?.sparks ??
      0
    ) || 0,

  runes:
    Number(
      data?.lifetimeReferralEarnings?.runes ??
      0
    ) || 0,
});

setReferralsStreak({

          enabled: !!data?.referralsStreak?.enabled,
          progress: Number(data?.referralsStreak?.progress ?? 0) || 0,
          milestones: Array.isArray(data?.referralsStreak?.milestones)
            ? data.referralsStreak.milestones
            : [],
        });
      } catch (err) {
        console.error("Invite modal load failed:", err);
        if (!cancelled) {
          setFriends([]);
setReferralCode("");
setReferralLink("");

setPendingRewards({
  sparks: 0,
  runes: 0,
});

setReferralEarnings({
  sparks: 0,
  runes: 0,
});

setReferralsStreak({
            enabled: false,
            progress: 0,
            milestones: [],
          });
        }
      } finally {
        if (!cancelled) setFriendsLoading(false);
      }
    }

   loadInviteData();

return () => {
  cancelled = true;
};
}, [open, showReferralsModal]);

const handleCopyReferralLink = async () => {
    if (!referralLink) return;

    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy referral link", err);
    }
  };

  
 const handleShareReferral = async () => {
  const tg = window.Telegram?.WebApp;

  if (!referralLink) return;

  if (
    !tg?.initDataUnsafe?.user?.id ||
    typeof tg.shareMessage !== "function"
  ) {
    try {
      await navigator.clipboard.writeText(referralLink);
    } catch (e) {
      console.error("fallback copy failed", e);
    }

    return;
  }

  const rawRef =
    referralLink.includes("startapp=")
      ? referralLink
          .split("startapp=")[1]
          ?.split("&")[0]
      : referralLink;

  let response;

  try {
    response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/telegram/prepare-message`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization:
            `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          userId: tg.initDataUnsafe.user.id,
          referralCode: rawRef,
        }),
      }
    );
  } catch (e) {
    console.error("prepare-message request failed", e);
    return;
  }

  let data = {};

  try {
    data = await response.json();
  } catch (e) {
    console.error("prepare-message invalid json", e);
    return;
  }

  if (
    !response.ok ||
    !data?.preparedMessageId
  ) {
    console.error("prepare-message failed", data);
    return;
  }

  try {
    tg.shareMessage(
      data.preparedMessageId,
      (success) => {
        console.log("shareMessage result:", success);
      }
    );
  } catch (e) {
    console.error("Telegram shareMessage failed", e);
  }
};
    if (!open) return null;

  return (
    <>
      <div
        className="modal_box"
      >
        <div className="modal_title">
          Invite Friends
          <button type="button" className="modal_close" onClick={onClose} />
        </div>

        <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.92, opacity: 0 }}>
          <div
            className="modal_inside_2"
          >
            <div
              style={{
                position: "relative",
                width: "100%",
                minHeight: 112,
                borderRadius: 8,
                background:
                  "linear-gradient(135deg, #35105f 0%, #871e79 55%, #40104f 100%)",
                border: "3px solid #ffeb4f",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                gap: 10,
                overflow: "hidden",
              }}
            >
              <div
  style={{
    width: 112,
    alignSelf: "stretch",
    flex: "0 0 112px",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
  }}
>
  <img
    src="/v2/share.png"
    alt=""
    style={{
      display: "block",
      width: "100%",
      height: "auto",
      maxHeight: "100%",
      objectFit: "contain",
    }}
    onError={(e) => {
      e.currentTarget.style.display = "none";
    }}
  />
</div>

              <div style={{ paddingTop: 10, paddingBottom: 10, flex: 1, minWidth: 0, textAlign: "left" }}>
                <div
                  className="shadow1"
                  style={{
                    color: "#ffeb4f",
                    fontSize: 20,
                    lineHeight: "23px",
                    fontWeight: 900,
                    marginBottom: 6,
                  }}
                >
                  How it Works?
                </div>

                <div
  className="shadow1"
  style={{
    color: "#fff",
    fontSize: 12,
    lineHeight: "16px",
    fontWeight: 700,
  }}
>
  <div style={{ marginBottom: 3 }}>
    Earn{" "}
    <span style={{ color: "#ffeb4f" }}>
      {runePurchaseCommissionPercent}%
    </span>{" "}
    from your qualified referrals' Rune purchases.
  </div>

  <div style={{ marginBottom: 3 }}>
    Earn{" "}
    <span style={{ color: "#ffeb4f" }}>
      {sparkPurchaseCommissionPercent}%
    </span>{" "}
    from your qualified referrals' Sparks purchases.
  </div>

  {(qualifiedReferralSparks > 0 ||
    qualifiedReferralRunes > 0) && (
    <div style={{ marginBottom: 3 }}>
      When a friend becomes qualified, you receive{" "}
      {qualifiedReferralSparks > 0 && (
        <>
          <span style={{ color: "#ffeb4f" }}>
            {formatNum(qualifiedReferralSparks)} Sparks
          </span>
          {qualifiedReferralRunes > 0 ? " + " : ""}
        </>
      )}

      {qualifiedReferralRunes > 0 && (
        <span style={{ color: "#ffeb4f" }}>
          {formatNum(qualifiedReferralRunes)} Runes
        </span>
      )}
      .
    </div>
  )}

  <div>
    Qualification:{" "}Play{" "}
    <span style={{ color: "#ffeb4f" }}>
      {referralBattlesRequired} battle
      {referralBattlesRequired === 1 ? "" : "s"}
    </span>

    {requireRunePurchase && (
      <>
        {" + "}
        <span style={{ color: "#ffeb4f" }}>
          Rune purchase
        </span>
      </>
    )}

    {requireSparkPurchase && (
      <>
        {" + "}
        <span style={{ color: "#ffeb4f" }}>
          Sparks purchase
        </span>
      </>
    )}
    .
  </div>
</div>
              </div>
            </div>

           {referralsStreak.enabled && milestones.length > 0 && (
  <div
    style={{
      width: "100%",
      marginTop: 8,
      marginBottom: 10,
      position: "relative",
      overflow: "hidden",
    }}
  >
    <div
      style={{
        display: "flex",
        gap: 6,

        overflowX: "auto",
        overflowY: "hidden",

        scrollSnapType: "x mandatory",
        WebkitOverflowScrolling: "touch",

        paddingBottom: 4,

        scrollbarWidth: "none",
        msOverflowStyle: "none",

        touchAction: "pan-x",
      }}
    >
      {milestones.map((milestone, index) => {
        const requiredFriends = Number(
          milestone.count || 0
        );

        const done =
          progress >= requiredFriends;

        const previousDone =
          index === 0 ||
          progress >= Number(
            milestones[index - 1]?.count || 0
          );

        return (
          <div
            key={
              milestone.itemId ||
              `${requiredFriends}-${index}`
            }
            style={{
              flex: `0 0 calc((100% - ${
                (referralMilestonesPerView - 1) * 6
              }px) / ${referralMilestonesPerView})`,

              minWidth: 0,
              scrollSnapAlign: "start",

              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* REQUIRED FRIENDS */}
            <div
              className="shadow1"
              style={{
                color: "#fff",
                fontSize: 16,
                lineHeight: "18px",
                fontWeight: 600,
                textAlign: "center",
                whiteSpace: "nowrap",
              }}
            >
              {requiredFriends}
            </div>

            <div
              className="shadow1"
              style={{
                color: "#fff",
                fontSize: 12,
                lineHeight: "12px",
                fontWeight: 800,
                textAlign: "center",
                marginTop: 1,
                marginBottom: 5,
                whiteSpace: "nowrap",
              }}
            >
              {requiredFriends === 1
                ? "Friend"
                : "Friends"}
            </div>

            {/* REWARD CARD */}
            <div
              style={{
                width: "100%",
                height: 70,

                backgroundImage: `url('${
                  done
                    ? "/v2/share_active.png"
                    : "/v2/share_inactive.png"
                }')`,

                backgroundSize: "100% 100%",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",

                position: "relative",

                display: "flex",
                alignItems: "center",
                justifyContent: "center",

                overflow: "hidden",
                boxSizing: "border-box",
              }}
            >
              {(() => {
                const item =
                  milestone?.item || {};

                const seasonalFragmentCount =
                  Number(
                    milestone?.amount ??
                    milestone?.quantity ??
                    milestone?.itemAmount ??
                    milestone?.item_amount ??
                    item?.amount ??
                    item?.quantity ??
                    0
                  );

                const isFullSeasonalCharacter =
                  milestone?.itemType ===
                    "seasonal_accessory" &&
                  seasonalFragmentCount === 1;

                if (isFullSeasonalCharacter) {
                  const seasonName =
                    item?.season_name ??
                    item?.seasonName ??
                    milestone?.season_name ??
                    milestone?.seasonName ??
                    item?.name ??
                    milestone?.itemName ??
                    milestone?.label ??
                    null;

                  return (
                    <div
                      style={{
                        width: 62,
                        height: 62,
                        marginTop: -4,

                        opacity: done
                          ? 1
                          : 0.65,

                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",

                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          transform:
                            "translateY(6px)",
                        }}
                      >
                        {renderReferralSkinPreview(
                          referralPreviewCard,
                          seasonName,
                          66
                        )}
                      </div>
                    </div>
                  );
                }

                return (
                  <img
                    src={
                      milestone.icon ||
                      "/pack.png"
                    }
                    alt={
                      milestone.label ||
                      "Referral reward"
                    }
                    style={{
                      width: 38,
                      height: 38,
                      objectFit: "contain",

                      opacity: done
                        ? 1
                        : 0.65,

                      marginTop: -7,
                    }}
                    onError={(event) => {
                      event.currentTarget.onerror =
                        null;

                      event.currentTarget.src =
                        "/pack.png";
                    }}
                  />
                );
              })()}

              {/* LOCK / CHECK */}
              <img
                src={
                  done
                    ? "/v2/check.png"
                    : "/v2/lock.png"
                }
                alt=""
                style={{
                  position: "absolute",
                  top: 6,
                  right: 5,

                  width: 15,
                  height: 15,

                  objectFit: "contain",
                  zIndex: 5,
                }}
              />

              {/* REWARD NAME */}
              <div
                className="shadow1"
                title={milestone.label}
                style={{
                  position: "absolute",
                  left: 3,
                  right: 3,
                  bottom: 3,

                  color: "#fff",

                  fontSize: 10,
                  lineHeight: "11px",
                  fontWeight: 800,

                  textAlign: "center",

                  padding: "3px 3px",

                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",

                  backgroundColor: "#503113",

                  boxSizing: "border-box",
                  zIndex: 6,
                }}
              >
                {milestone.label}
              </div>
            </div>

            {/* PROGRESS MARKER */}
            <div
              style={{
                width: "100%",
                height: 34,

                position: "relative",

                display: "flex",
                justifyContent: "center",
                alignItems: "center",

                marginTop: 3,
              }}
            >
              {/* LINE BEFORE */}
              {index > 0 && (
                <div
                  style={{
                    position: "absolute",

                    left: 0,
                    right: "50%",

                    top: 13,
                    height: 8,

                    background: previousDone
                      ? "linear-gradient(90deg, #5ccfff, #276ee8)"
                      : "#2b1005",

                    borderTop:
                      "2px solid #55260f",

                    borderBottom:
                      "2px solid #55260f",

                    boxSizing: "border-box",
                  }}
                />
              )}

              {/* LINE AFTER */}
              {index <
                milestones.length - 1 && (
                <div
                  style={{
                    position: "absolute",

                    left: "50%",
                    right: 0,

                    top: 13,
                    height: 8,

                    background: done
                      ? "linear-gradient(90deg, #5ccfff, #276ee8)"
                      : "#2b1005",

                    borderTop:
                      "2px solid #55260f",

                    borderBottom:
                      "2px solid #55260f",

                    boxSizing: "border-box",
                  }}
                />
              )}

              {/* NUMBER */}
              <div
                className="shadow1"
                title={`${requiredFriends} ${
                  requiredFriends === 1
                    ? "Friend"
                    : "Friends"
                }`}
                style={{
                  position: "relative",
                  zIndex: 5,

                  width: 30,
                  height: 30,

                  borderRadius: "50%",

                  background: done
                    ? "#3f751b"
                    : "#6d310e",

                  border:
                    "3px solid #ffd36a",

                  color: "#fff",

                  fontSize: 16,
                  lineHeight: "24px",

                  textAlign: "center",
                  fontWeight: 900,

                  boxSizing: "border-box",
                }}
              >
                {index + 1}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
)}

            <div
              className="shadow1"
              style={{
                color: "#fff",
                fontSize: 16,
                lineHeight: "18px",
                fontWeight: 600,
                textAlign: "center",
                marginBottom: 0,
              }}
            >
              YOUR REFERRAL CODE
            </div>

            <div
            className="info"
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 10,
              }}
            >
              <div
                className="shadow1"
                style={{
                  flex: 1,
                  minWidth: 0,
                  color: "#fff",
                  fontSize: 16,
                  lineHeight: "32px",
                  fontWeight: 900,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textAlign: "left",
                }}
              >
                {friendsLoading
                  ? "LOADING..."
                  : referralCode
                    ? referralCode
                    : "NO CODE"}
              </div>

              <button
                type="button"
                className="game-button-small yellow-button"
                onClick={handleCopyReferralLink}
                disabled={!referralLink}
                style={{
                  width: 58,
                  minWidth: 58,
                  fontSize: 13,
                  opacity: referralLink ? 1 : 0.55,
                }}
              >
                {copied ? "OK" : "COPY"}
              </button>
            </div>

            <button
  type="button"
  className="game-button green-button"
  onClick={handleShareReferral}
  style={{
    marginTop: 5,
    fontSize: 16,
    opacity: referralLink ? 1 : 0.55,
  }}
>
  SHARE INVITE
</button>

            </div>

            <div
              className="modal_footer"
            >
              <button
  type="button"
  className="game-button yellow-button"
  onClick={() => {
    onClose?.();
    onCloseProfile?.();

    onNavigate?.("leaderboard", {
      initialView: "recruiters",
    });
  }}
  style={{
    marginRight: 10,
    fontSize: 15,
  }}
>
  LEADERBOARD
</button>

             <button
  type="button"
  className="game-button yellow-button"
  onClick={() => {
    setShowReferralsModal(true);
  }}
  style={{
    fontSize: 15,
  }}
>
  REFERRALS
</button>
                       </div>
        </motion.div>
      </div>

      {showReferralsModal && (
        <div
          className="modal_box"
        >
          <div className="modal_title">
            Your Referrals

            <button
              type="button"
              className="modal_close"
              onClick={() => setShowReferralsModal(false)}
            />
          </div>

          <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.92, opacity: 0 }}>
            <div
              className="modal_inside_2"
              style={{
                boxSizing: "border-box",
                paddingTop: 5
              }}
            >

<div
  style={{
    display: "grid",
    gridTemplateColumns:
      "repeat(3, 1fr)",
    gap: 6,
    marginBottom: 10,
  }}
>
  <div
    className="info"
    style={{
      textAlign: "center",
      padding: 7,
    }}
  >
    <div
      className="shadow1"
      style={{
        color: "#fff",
        fontSize: 11,
        fontWeight: 800,
      }}
    >
      TOTAL
    </div>

    <div
      className="shadow1"
      style={{
        color: "#ffdf62",
        fontSize: 18,
        fontWeight: 900,
      }}
    >
      {friends.length}
    </div>
  </div>

  <div
    className="info"
    style={{
      textAlign: "center",
      padding: 7,
    }}
  >
    <div
      className="shadow1"
      style={{
        color: "#fff",
        fontSize: 11,
        fontWeight: 800,
      }}
    >
      ACTIVE
    </div>

    <div
      className="shadow1"
      style={{
        color: "#9aff82",
        fontSize: 18,
        fontWeight: 900,
      }}
    >
      {activeReferrals}
    </div>
  </div>

  <div
    className="info"
    style={{
      textAlign: "center",
      padding: 7,
    }}
  >
    <div
      className="shadow1"
      style={{
        color: "#fff",
        fontSize: 11,
        fontWeight: 800,
      }}
    >
      PENDING
    </div>

    <div
      className="shadow1"
      style={{
        color: "#ffc47d",
        fontSize: 18,
        fontWeight: 900,
      }}
    >
      {pendingReferrals}
    </div>
  </div>
</div>

<div
  className="info"
  style={{
    width: "100%",
    boxSizing: "border-box",
    marginBottom: 10,
    padding: "8px 10px",
    textAlign: "center",
  }}
>
  <div
    className="shadow1"
    style={{
      color: "#fff",
      fontSize: 11,
      fontWeight: 800,
      marginBottom: 7,
    }}
  >
    TOTAL EARNED
  </div>

  <div
    style={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 8,
      alignItems: "center",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 5,
        minWidth: 0,
      }}
    >
      <img
        src="/sparks.png"
        alt="Sparks"
        style={{
          display: "block",
          width: 22,
          height: 22,
          objectFit: "contain",
          flex: "0 0 22px",
        }}
      />

      <div
        className="shadow1"
        style={{
          color: "#ffdf62",
          fontSize: 18,
          lineHeight: "22px",
          fontWeight: 900,
          minWidth: 0,
        }}
      >
        {formatNum(
          referralEarnings.sparks
        )}
      </div>
    </div>

    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 5,
        minWidth: 0,
      }}
    >
      <img
        src="/runes.png"
        alt="Runes"
        style={{
          display: "block",
          width: 22,
          height: 22,
          objectFit: "contain",
          flex: "0 0 22px",
        }}
      />

      <div
        className="shadow1"
        style={{
          color: "#9aff82",
          fontSize: 18,
          lineHeight: "22px",
          fontWeight: 900,
          minWidth: 0,
        }}
      >
        {formatNum(
          referralEarnings.runes
        )}
      </div>
    </div>
  </div>

  <div
    className="shadow1"
    style={{
      color: "#fff",
      opacity: 0.8,
      fontSize: 10,
      lineHeight: "12px",
      fontWeight: 700,
      marginTop: 4,
    }}
  >
    LIFETIME REFERRAL COMMISSIONS
  </div>
</div>

{friendsLoading ? (
                <div
                  style={{
                    Height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <LoadingSpinner
                    text="Loading Referrals"
                    size={52}
                  />
                </div>
              ) : friends.length === 0 ? (


                <div
                  className="info shadow1"
                  style={{
                    color: "#fff",
                    fontSize: 16,
                    lineHeight: "20px",
                    fontWeight: 600,
                    textAlign: "center",
                  }}
                >
                  You Have No Referrals Yet.
                  <br />
                  Share Your Invite Code With Your Friends!
                </div>
              ) : (
                <div
                  style={{
                    width: "100%",
                    maxHeight: "100vh",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  {friends.map((friend, index) => {
                    const friendName =
                      friend?.name ||
                      friend?.username ||
                      "Unknown Player";

                    const friendAvatar =
                      friend?.avatarUrl ||
                      friend?.avatar_url ||
                      friend?.avatar ||
                      svgFallback(friendName);

                    const joinedDate =
                      friend?.created_at ||
                      friend?.createdAt ||
                      null;

                      const referralStatus = String(
  friend?.status || "pending"
)
  .trim()
  .toLowerCase();

const referralActive =
  referralStatus === "qualified";

                    return (
                      <motion.div
                        key={
                          friend?.id ||
                          friend?.user_id ||
                          friend?.userId ||
                          index
                        }
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                          duration: 0.25,
                          delay: Math.min(index * 0.04, 0.4),
                        }}
                        className="info"
                        style={{
                          width: "100%",
                          minWidth: 0,
                          minHeight: 58,
                          padding: "8px 8px",
                          boxSizing: "border-box",
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: 38,
                            height: 38,
                            flex: "0 0 38px",
                            borderRadius: 8,
                            overflow: "hidden",
                            backgroundColor: "#000",
                          }}
                        >
                          <img
                            src={friendAvatar}
                            alt=""
                            loading="lazy"
                            onError={(event) => {
                              event.currentTarget.onerror = null;
                              event.currentTarget.src =
                                svgFallback(friendName);
                            }}
                            style={{
                              display: "block",
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        </div>

                        <div
                          style={{
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textAlign: "left",
                          }}
                        >
                          <div
                            className="shadow1"
                            title={friendName}
                            style={{
                              color: "#ffcc62",
                              fontSize: 18,
                              lineHeight: "21px",
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {friendName}
                          </div>

                          <div
                            className="shadow1"
                            style={{
                              color: "#fff",
                              fontSize: 12,
                              lineHeight: "16px",
                              fontWeight: 600,
                            }}
                          >
                            Joined:{" "}
                            {joinedDate
                              ? new Date(
                                  joinedDate
                                ).toLocaleDateString()
                              : "—"}
                          </div>
                        </div>

                        <div
  className="shadow1"
  style={{
    flex: "0 0 auto",
    minWidth: 72,

    padding: "5px 8px",

    borderRadius: 6,

    background:
      referralActive
        ? "rgba(52, 122, 48, 0.9)"
        : "rgba(121, 68, 25, 0.9)",

    border:
      referralActive
        ? "2px solid #8ee878"
        : "2px solid #d79545",

    color:
      referralActive
        ? "#d8ffd0"
        : "#ffd49a",

    fontSize: 11,
    lineHeight: "14px",
    fontWeight: 900,

    textAlign: "center",

    whiteSpace: "nowrap",

    boxSizing: "border-box",
  }}
>
  {referralActive
    ? "ACTIVE"
    : "PENDING"}
</div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="modal_footer">
              <button
                type="button"
                className="game-button red-button"
                onClick={() => setShowReferralsModal(false)}
                style={{
                  fontSize: 15,
                }}
              >
                BACK
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}

function normalizeCosmeticCategory(value) {
  const category = String(value || "")
    .trim()
    .toLowerCase();

  if (
    category === "style" ||
    category === "styles"
  ) {
    return "styles";
  }

  if (
    category === "banner" ||
    category === "banners"
  ) {
    return "banners";
  }

  if (
    category === "emoji" ||
    category === "emogy" ||
    category === "emojy"
  ) {
    return "emoji";
  }

  return "";
}

function getCosmeticCategory(inv) {
  return normalizeCosmeticCategory(
    inv?.item?.name_category ||
      inv?.item?.nameCategory
  );
}

function getCosmeticUserColumn(inv) {
  const category = getCosmeticCategory(inv);

  if (category === "styles") {
    return "active_name_style";
  }

  if (category === "banners") {
    return "active_name_banner";
  }

  if (category === "emoji") {
    return "active_name_emogy";
  }

  return null;
}

function getCosmeticValue(inv) {
  return String(
    inv?.item?.name_value ||
      inv?.item?.nameValue ||
      ""
  ).trim();
}

function getCosmeticName(inv) {
  return (
    inv?.item?.display_name ||
    inv?.item?.name ||
    inv?.item?.label ||
    getCosmeticValue(inv) ||
    "Cosmetic"
  );
}

function getCosmeticImage(inv) {
  const item = inv?.item || {};

  const rawImage = String(
    item?.name_value ??
    item?.nameValue ??
    item?.image ??
    item?.icon ??
    item?.name_image ??
    item?.nameImage ??
    ""
  ).trim();

  if (rawImage) {
    if (
      rawImage.startsWith("/") ||
      rawImage.startsWith("http://") ||
      rawImage.startsWith("https://") ||
      rawImage.startsWith("data:")
    ) {
      return rawImage;
    }

    return `/name-cosmetics/${rawImage}`;
  }

  const category = getCosmeticCategory(inv);

  if (category === "banners") {
    return "/banner_icon.png";
  }

  if (category === "emoji") {
    return "/emogy_icon.png";
  }

  return "/style_icon.png";
}

    function getCosmeticImageStyle(inv) {
  const category = getCosmeticCategory(inv);

  if (category === "styles") {
    return {
      width: 72,
      height: 72,
      maxWidth: 72,
      maxHeight: 72,
      marginTop: -12,
      marginBottom: -7,
    };
  }

  if (category === "emoji") {
    return {
      width: 46,
      height: 46,
      maxWidth: 46,
      maxHeight: 46,
      marginTop: 0,
      marginBottom: 5,
    };
  }

  if (category === "banners") {
    return {
      width: 70,
      height: 70,
      maxWidth: 70,
      maxHeight: 70,
      marginTop: -21,
      marginBottom: -16,
    };
  }

  return {
    width: 48,
    height: 48,
    maxWidth: 48,
    maxHeight: 48,
    marginTop: 0,
    marginBottom: 5,
  };
}

function renderCosmeticPreview(inv) {
  const category = getCosmeticCategory(inv);

  if (category === "styles") {
    return (
      <div
        style={{
          width: "100%",
          minHeight: 72,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "0 5px",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            maxWidth: "100%",
            fontSize: 16,
            lineHeight: "22px",
            fontWeight: 800,
            textAlign: "center",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            ...getNameStyleCSS(getCosmeticValue(inv)),
          }}
        >
          TapTopia
        </div>
      </div>
    );
  }

  const cosmeticImageStyle = getCosmeticImageStyle(inv);

  return (
    <img
      src={getCosmeticImage(inv)}
      alt={getCosmeticName(inv)}
      draggable={false}
      onError={(event) => {
        event.currentTarget.onerror = null;
        event.currentTarget.src =
          category === "banners"
            ? "/banner_icon.png"
            : "/emogy_icon.png";
      }}
      style={{
        objectFit: "contain",
        display: "block",
        filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.7))",
        ...cosmeticImageStyle,
      }}
    />
  );
}

function isCosmeticActive(inv, user) {
  const column = getCosmeticUserColumn(inv);

  if (!column) {
    return false;
  }

  const activeValue = String(
    user?.[column] || ""
  ).trim();

  const cosmeticValue = getCosmeticValue(inv);

  return Boolean(
    activeValue &&
      cosmeticValue &&
      activeValue === cosmeticValue
  );
}

function NameCosmeticsModal({
  open,
  user,
  setUser,
  onClose,
  onCloseProfile,
  onNavigate,
}) {
  const [cosmetics, setCosmetics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busyItemId, setBusyItemId] = useState(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;

    const loadCosmetics = async () => {
      try {
        setLoading(true);
        setError("");

        const token = localStorage.getItem("token");

        if (!token) {
          throw new Error("Not logged in.");
        }

        const res = await fetch(
          `${API_BASE}/api/inventory`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "X-TG-INITDATA":
                window.Telegram?.WebApp?.initData || "",
            },
          }
        );

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(
            data?.error || "Failed to load cosmetics."
          );
        }

        if (cancelled) {
          return;
        }

        const inventoryItems = Array.isArray(data?.items)
          ? data.items
          : [];

        const ownedCosmetics = inventoryItems.filter(
          (inv) => {
            const type = String(
              inv?.item?.type || ""
            )
              .trim()
              .toLowerCase();

            const isNameItem =
              type === "name" ||
              type === "cosmetic" ||
              type === "name_cosmetic";

            return (
              isNameItem &&
              Boolean(getCosmeticCategory(inv))
            );
          }
        );

        setCosmetics(ownedCosmetics);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.message || "Failed to load cosmetics."
          );
          setCosmetics([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadCosmetics();

    return () => {
      cancelled = true;
    };
  }, [open]);

  const toggleCosmetic = async (inv) => {
    if (
      !inv?.user_item_id ||
      busyItemId
    ) {
      return;
    }

    const currentlyActive = isCosmeticActive(
      inv,
      user
    );

    const action = currentlyActive
      ? "remove"
      : "equip";

    try {
      setBusyItemId(inv.user_item_id);
      setError("");

      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("Not logged in.");
      }

      const res = await fetch(
        `${API_BASE}/api/inventory/equip-name-item`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-TG-INITDATA":
              window.Telegram?.WebApp?.initData || "",
          },
          body: JSON.stringify({
            userItemId: inv.user_item_id,
            action,
          }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data?.error ||
            "Failed to update cosmetic."
        );
      }

      if (
        data?.user &&
        typeof setUser === "function"
      ) {
        setUser((previousUser) => ({
          ...(previousUser || {}),
          ...(data.user || {}),
        }));
      }
    } catch (err) {
      setError(
        err.message || "Failed to update cosmetic."
      );
    } finally {
      setBusyItemId(null);
    }
  };

  const cosmeticGroups = [
    {
      key: "styles",
      title: "Name Styles",
      icon: "/style_icon.png",
    },
    {
      key: "banners",
      title: "Banners",
      icon: "/banner_icon.png",
    },
    {
      key: "emoji",
      title: "Emoji",
      icon: "/emogy_icon.png",
    },
  ];

  if (!open) {
    return null;
  }

  return (
    <div
      className="modal_box"
    >
      <div className="modal_title">
        Name Cosmetics

        <button
          type="button"
          className="modal_close"
          onClick={() => {
            if (!busyItemId) {
              onClose?.();
            }
          }}
        />
      </div>

      <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.92, opacity: 0 }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 20,
      }}>
        <div
          className="modal_inside_2"
          style={{
            padding: 10,
            paddingBottom: 0,
            boxSizing: "border-box",
          }}
        >
          {loading && (
            <div
              style={{
                minHeight: 320,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LoadingSpinner
                text="Loading Cosmetics"
                size={58}
              />
            </div>
          )}

          {!loading && error && (
            <div
              className="info shadow1"
              style={{
                color: "#ff765f",
                fontSize: 15,
                fontWeight: 700,
                textAlign: "center",
                marginBottom: 12,
              }}
            >
              {error}
            </div>
          )}

          {!loading &&
            cosmeticGroups.map((group) => {
              const groupItems = cosmetics.filter(
                (inv) =>
                  getCosmeticCategory(inv) ===
                  group.key
              );

              return (
                <div
                  key={group.key}
                  style={{
                    width: "100%",
                    marginBottom: 12,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: 42,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "0 10px",
                      background: "#87401f",
                      border: "3px solid #5d2a13",
                      borderRadius: "8px 8px 0 0",
                      boxSizing: "border-box",
                    }}
                  >
                    <img
                      src={group.icon}
                      alt=""
                      style={{
                        width: 25,
                        height: 25,
                        objectFit: "contain",
                      }}
                    />

                    <div
                      className="shadow1"
                      style={{
                        flex: 1,
                        color: "#fff",
                        fontSize: 17,
                        fontWeight: 700,
                        textAlign: "left",
                      }}
                    >
                      {group.title}
                    </div>

                    <div
                      className="shadow1"
                      style={{
                        color: "#ffdf5f",
                        fontSize: 14,
                        fontWeight: 700,
                      }}
                    >
                      {groupItems.length}
                    </div>
                  </div>

                  <div
                    style={{
                      width: "100%",
                      padding: 8,
                      background:
                        "rgba(80, 50, 25, 0.58)",
                      borderLeft:
                        "3px solid #5d2a13",
                      borderRight:
                        "3px solid #5d2a13",
                      borderBottom:
                        "3px solid #5d2a13",
                      borderRadius: "0 0 8px 8px",
                      boxSizing: "border-box",
                    }}
                  >
                    {groupItems.length === 0 ? (
                      <div
                        className="shadow1"
                        style={{
                          padding: "14px 8px",
                          color:
                            "rgba(255,255,255,0.72)",
                          fontSize: 14,
                          fontWeight: 600,
                          textAlign: "center",
                        }}
                      >
                        You do not own any{" "}
                        {group.title.toLowerCase()} yet.
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(3, minmax(0, 1fr))",
                          gap: 8,
                        }}
                      >
                       {groupItems.map((inv) => {
  const active = isCosmeticActive(inv, user);

  const busy =
    busyItemId === inv.user_item_id;

  return (
                            <div
                              key={inv.user_item_id}
                              onClick={() => {
                                if (!busyItemId) {
                                  toggleCosmetic(inv);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                              style={{
                                minWidth: 0,
                                minHeight: 125,
                                position: "relative",
                                padding: 8,
                                paddingBottom: 15,
                                boxSizing: "border-box",
                                backgroundImage:
                                  'url("/v2/item.png")',
                                backgroundRepeat: "repeat",
                                backgroundSize:
                                  "100% 100%",
                                backgroundPosition:
                                  "center",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                textAlign: "center",
                                cursor: busyItemId
                                  ? "default"
                                  : "pointer",
                                opacity:
                                  busyItemId && !busy
                                    ? 0.65
                                    : 1,
                                filter: active
                                  ? "drop-shadow(0 0 7px rgba(87,255,107,0.9))"
                                  : "none",
                              }}
                            >
                              {active && (
                                <div
                                  className="shadow1"
                                  style={{
                                    position: "absolute",
                                    top: 5,
                                    right: 5,
                                    zIndex: 5,
                                    padding: "2px 5px",
                                    borderRadius: 5,
                                    background:
                                      "rgba(31,150,50,0.95)",
                                    color: "#fff",
                                    fontSize: 9,
                                    fontWeight: 800,
                                  }}
                                >
                                  ACTIVE
                                </div>
                              )}

                              {renderCosmeticPreview(inv)}

                              <div className="claim_bt shadow1">
                                {busy
                                  ? "..."
                                  : active
                                    ? "DISABLE"
                                    : "USE IT"}
                              </div>

                              <div
                                className="shadow1"
                                title={getCosmeticName(inv)}
                                style={{
                                  width: "100%",
                                  color: "#fff",
                                  fontSize: 13,
                                  lineHeight: "14px",
                                  fontWeight: 600,
                                  overflow: "hidden",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient:
                                    "vertical",
                                }}
                              >
                                {getCosmeticName(inv)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
        </div>

       <div className="modal_footer">
  <button
  type="button"
  className="game-button green-button"
  disabled={Boolean(busyItemId)}
  onClick={() => {
    if (busyItemId) return;

    onClose?.();
    onCloseProfile?.();

    onNavigate?.("shop", {
      initialView: "items",
    });
  }}
  style={{
    marginRight: 10,
    opacity: busyItemId ? 0.55 : 1,
    cursor: busyItemId ? "not-allowed" : "pointer",
  }}
>
  BUY ITEMS
</button>

  <button
    type="button"
    className="game-button red-button"
    disabled={Boolean(busyItemId)}
    onClick={onClose}
    style={{
      opacity: busyItemId ? 0.55 : 1,
      cursor: busyItemId ? "not-allowed" : "pointer",
    }}
  >
    CLOSE
  </button>
</div>
      </motion.div>
    </div>
  );
}

export default function ProfileModal({
  open,
  user,
  setUser,
  onClose,
  onInviteFriend,
  onIncreaseLevel,
  onOpenDefenseDeck,
  onNavigate,
  onOpenShop,
  hasBattlePass = false,
  battlePassLoading = false,
}) {

const [tonConnectUI] = useTonConnectUI();
const tonWallet = useTonWallet();
const tonWalletAddress = useTonAddress(true);

const [walletCopied, setWalletCopied] = useState(false);

const [walletSaving, setWalletSaving] = useState(false);
const [walletSaveError, setWalletSaveError] = useState("");
const [showEmailModal, setShowEmailModal] = useState(false);
const [publicProfileSaving, setPublicProfileSaving] = useState(false);
const [publicProfileError, setPublicProfileError] = useState("");

            const [
  publicProfileEnabled,
  setPublicProfileEnabled,
] = useState(true);

const [
  publicProfileLoading,
  setPublicProfileLoading,
] = useState(false);

const [emailStatus, setEmailStatus] = useState({
  loading: true,
  email: null,
  verified: false,
  rewardAlreadyPaid: false,
  reward: {
    currency: "sparks",
    amount: 500,
  },
});

  const [cards, setCards] = useState([]);
  const [deckCardIds, setDeckCardIds] = useState([]);
  const [deckSize, setDeckSize] = useState(FALLBACK_DECK_SIZE);
  const [deckLoading, setDeckLoading] = useState(false);

  const { shop,season } = useAppConfig();

  const [showSideModal, setShowSideModal] = useState(false);
  const [sideTarget, setSideTarget] = useState(null);
  const [sideLoading, setSideLoading] = useState(false);
  const [sideUseBypass, setSideUseBypass] = useState(false);

  const [showSideSuccessModal, setShowSideSuccessModal] = useState(false);
  const [sideErrorMessage, setSideErrorMessage] = useState("");

  const [profileWins, setProfileWins] = useState(0);

  const [showInviteFriendsModal, setShowInviteFriendsModal] = useState(false);
  const [showMyTapisModal, setShowMyTapisModal] = useState(false);
            const [
  showNameCosmeticsModal,
  setShowNameCosmeticsModal,
] = useState(false);

  const changeSidePrice = shop?.changeSidePrice ?? 0;
  const SIDE_CHANGE_BYPASS_RUNES_PRICE = 25;
  const SIDE_CHANGE_COOLDOWN_HOURS = 24;

  const name =
  user?.name ||
  user?.username ||
  "Player";

const avatar =
  user?.avatarUrl ||
  user?.avatar_url ||
  user?.photo_url ||
  user?.avatar ||
  svgFallback(name);


const currentSeasonNumber =
  Number(
    season?.season_number ??
    season?.seasonNumber ??
    season?.number ??
    season?.id ??
    season
  );

const gamePassSeasonNumber =
  Number(
    user?.gamepass_season_id ??
    user?.gamepassSeasonId ??
    user?.game_pass_season_id
  );

const hasGamePass =
  Boolean(
    user?.gamepass_active ??
    user?.gamepassActive ??
    user?.game_pass_active
  ) &&
  Number.isFinite(currentSeasonNumber) &&
  Number.isFinite(gamePassSeasonNumber) &&
  gamePassSeasonNumber ===
    currentSeasonNumber;

const openPassPage = (page) => {
  onClose?.();
  onNavigate?.(page);
};

const {
  bannerSrc,
  emojiSrc,
} = getUserNameCosmetics(user);

const profileBannerStyle = bannerSrc
  ? getUserBannerStyle(user)
  : {
      backgroundColor: "#6a4628",
      backgroundImage: "none",
    };

  const level = Math.max(0, Math.min(Number(user?.level) || 0, 30));
  const levelPercent = Math.max(0, Math.min(100, (level / 30) * 100));

  const trophy = Number(
    user?.trophy ?? user?.trophies ?? user?.stats?.trophy ?? 0
  );

  const side = String(user?.side || "good").toLowerCase();
const isMoon = side === "bad" || side === "moon";

const currentSide = isMoon ? "moon" : "sun";
const nextSide = currentSide === "sun" ? "moon" : "sun";

const sideLabel = isMoon
  ? "Moon Anubis"
  : "Sun Pharaohs";
const sideIcon = isMoon ? "/v2/bad.png" : "/v2/good.png";

const nextSideLabel = nextSide === "sun" ? "Sun Side" : "Moon Side";
const nextSideStored = nextSide === "sun" ? "good" : "bad";

const arenaSideLabel =
  side === "bad" || side === "moon"
    ? "Anubis"
    : side === "good" || side === "sun"
      ? "Pharaohs"
      : side || "";

const getSideCooldownRemainingMs = () => {
  const last = user?.last_side_change_at ?? user?.lastSideChangeAt;
  if (!last) return 0;

  const lastMs = new Date(last).getTime();
  if (!Number.isFinite(lastMs)) return 0;

  const cooldownMs = SIDE_CHANGE_COOLDOWN_HOURS * 60 * 60 * 1000;
  const remaining = cooldownMs - (Date.now() - lastMs);

  return remaining > 0 ? remaining : 0;
};

const formatSideCooldown = (ms) => {
  const totalMinutes = Math.ceil(ms / (60 * 1000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours && minutes) return `${hours}h ${minutes}m`;
  if (hours) return `${hours}h`;
  return `${minutes}m`;
};

const loadEmailStatus = async () => {
  try {
    const token = localStorage.getItem("token");

    if (!token) return;

    const res = await fetch(
      `${API_BASE}/api/email/status`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-TG-INITDATA":
            window.Telegram?.WebApp?.initData || "",
        },
      }
    );

    const data = await res
      .json()
      .catch(() => ({}));

    if (!res.ok) {
      throw new Error(
        data?.error ||
          "Failed to load email status"
      );
    }

    setEmailStatus({
      loading: false,
      email: data?.email || null,
      verified: Boolean(data?.verified),
      verifiedAt:
        data?.verifiedAt || null,
      rewardAlreadyPaid:
        Boolean(
          data?.rewardAlreadyPaid
        ),
      reward: {
        currency:
          data?.reward?.currency ||
          "sparks",

        amount:
          Number(
            data?.reward?.amount || 0
          ),
      },
    });
  } catch (error) {
    console.error(
      "[PROFILE EMAIL STATUS ERROR]",
      error
    );

    setEmailStatus((previous) => ({
      ...previous,
      loading: false,
    }));
  }
};


useEffect(() => {
  if (!open) return;

  loadEmailStatus();
}, [open]);

useEffect(() => {
  if (!user?.id) return;
  if (!tonWalletAddress) return;

  const connectedWallet = String(tonWalletAddress).trim();
  const savedWallet = String(user?.wallet || "").trim();

  if (!connectedWallet) return;

  let cancelled = false;

  async function saveConnectedWallet() {
    try {
      setWalletSaving(true);
      setWalletSaveError("");

      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("Missing authentication token");
      }

      const res = await fetch(
  `${API_BASE}/api/shop/wallet`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-TG-INITDATA":
              window.Telegram?.WebApp?.initData || "",
          },
          body: JSON.stringify({
            wallet: connectedWallet,
          }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data?.error ||
          `Failed to save wallet: HTTP ${res.status}`
        );
      }

      if (cancelled) return;

      setUser?.((previousUser) => ({
        ...(previousUser || {}),
        wallet: data?.wallet ?? connectedWallet,
      }));
    } catch (error) {
      console.error(
        "[PROFILE WALLET SAVE ERROR]",
        error
      );

      if (!cancelled) {
        setWalletSaveError(
          error?.message || "Failed to save wallet"
        );
      }
    } finally {
      if (!cancelled) {
        setWalletSaving(false);
      }
    }
  }

  saveConnectedWallet();

  return () => {
    cancelled = true;
  };
}, [
  tonWalletAddress,
  user?.id,
  user?.wallet,
  setUser,
]);

const sideCooldownRemainingMs = getSideCooldownRemainingMs();
const isSideCooldownActive = sideCooldownRemainingMs > 0;

const canAffordSideChange =
  changeSidePrice > 0 && Number(user?.sparks || 0) >= changeSidePrice;

const canAffordSideBypass =
  Number(user?.runes || 0) >= SIDE_CHANGE_BYPASS_RUNES_PRICE;

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    

    async function loadDefenseDeckPreview() {
      try {
        setDeckLoading(true);

        const token = localStorage.getItem("token");
        if (!token) return;

        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
        };

        const [cardsRes, decksRes, configRes] = await Promise.all([
          fetch(`${API_BASE}/api/shop/mine`, { headers }),
          fetch(`${API_BASE}/api/pvp/defense-decks`, { headers }),
          fetch(`${API_BASE}/api/pvp/config`, { headers }),
        ]);

        const [cardsJson, decksJson, configJson] = await Promise.all([
          cardsRes.json().catch(() => ({})),
          decksRes.json().catch(() => ({})),
          configRes.json().catch(() => ({})),
        ]);

        if (cancelled) return;

        const nextDeckSize = Number(
          configJson?.defenseDeckSize ||
            decksJson?.defenseDeckSize ||
            FALLBACK_DECK_SIZE
        );

        const nextDeckCardIds = Array.isArray(
          decksJson?.decks?.[OVERALL_DECK_KEY]?.cardIds
        )
          ? decksJson.decks[OVERALL_DECK_KEY].cardIds.map(Number)
          : [];

        setCards(Array.isArray(cardsJson.cards) ? cardsJson.cards : []);
        setDeckCardIds(nextDeckCardIds);
        setDeckSize(nextDeckSize > 0 ? nextDeckSize : FALLBACK_DECK_SIZE);
      } catch (err) {
        console.warn("Profile defense deck preview failed:", err);
      } finally {
        if (!cancelled) setDeckLoading(false);
      }
    }

    loadDefenseDeckPreview();

    return () => {
      cancelled = true;
    };
  }, [open]);

  const defensePreviewCards = useMemo(() => {
    return deckCardIds
      .slice(0, deckSize || PREVIEW_LIMIT)
      .map((id) => cards.find((card) => Number(card.id) === Number(id)))
      .slice(0, PREVIEW_LIMIT);
  }, [deckCardIds, deckSize, cards]);

          const openMyTapis = () => {
  onClose?.();
  onNavigate?.("mytapis");
};

  const openChangeSideModal = () => {
  setSideTarget(nextSide);
  setSideUseBypass(false);
  setSideErrorMessage("");
  setShowSideModal(true);
};

const closeChangeSideModal = () => {
  if (sideLoading) return;

  setShowSideModal(false);
  setSideTarget(null);
  setSideUseBypass(false);
  setSideErrorMessage("");
};

const changeSide = async ({ bypassCooldown = false } = {}) => {
  if (!sideTarget || sideLoading) return;

  setSideLoading(true);
  setSideErrorMessage("");

  try {
    const res = await fetch(`${API_BASE}/api/shop/change-side`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
      },
      body: JSON.stringify({
        targetSide: sideTarget,
        bypassCooldown,
      }),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      setSideErrorMessage(data?.error || "Could not change side");
      return;
    }

    setUser?.((prev) => ({
      ...prev,
      side: data.user?.side ?? nextSideStored,
      runes: data.user?.runes ?? prev.runes,
      sparks: data.user?.sparks ?? prev.sparks,
      last_side_change_at:
        data.user?.last_side_change_at ??
        data.user?.lastSideChangeAt ??
        prev.last_side_change_at ??
        prev.lastSideChangeAt,
      lastSideChangeAt:
        data.user?.lastSideChangeAt ??
        data.user?.last_side_change_at ??
        prev.lastSideChangeAt ??
        prev.last_side_change_at,
    }));

    setShowSideModal(false);
    setSideTarget(null);
    setSideUseBypass(false);
    setShowSideSuccessModal(true);
  } catch (err) {
    console.error("Profile change side failed:", err);
    setSideErrorMessage("Network error");
  } finally {
    setSideLoading(false);
  }
};

        useEffect(() => {
  if (!open || !user?.id) return;

  let cancelled = false;

  async function loadProfileWins() {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("Missing authentication token.");
      }

      const res = await fetch(
        `${API_BASE}/api/season-stats/leaderboard/my-rank?metric=wins`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-TG-INITDATA":
              window.Telegram?.WebApp?.initData || "",
          },
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data?.error ||
          `Failed to load wins: HTTP ${res.status}`
        );
      }

      console.log("[PROFILE WINS RESPONSE]", data);

      if (cancelled) return;

      setProfileWins(
        Number(data?.wins ?? 0) || 0
      );
    } catch (err) {
      console.error("[PROFILE WINS ERROR]", err);

      if (!cancelled) {
        setProfileWins(0);
      }
    }
  }

  loadProfileWins();

  return () => {
    cancelled = true;
  };
}, [open, user?.id]);


          useEffect(() => {
  if (!open) return;

  let cancelled = false;

  async function loadPublicProfileSetting() {
    try {
      setPublicProfileLoading(true);
      setPublicProfileError("");

      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error(
          "Missing authentication token."
        );
      }

      const res = await fetch(
        `${API_BASE}/api/users/me/public-profile`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-TG-INITDATA":
              window.Telegram?.WebApp?.initData || "",
          },
        }
      );

      const data = await res
        .json()
        .catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data?.error ||
            "Could not load public profile setting."
        );
      }

      if (cancelled) return;

      setPublicProfileEnabled(
        data?.publicProfile !== false
      );

      setUser?.((previousUser) => ({
        ...(previousUser || {}),
        public_profile:
          data?.publicProfile !== false,
      }));
    } catch (error) {
      console.error(
        "[PUBLIC PROFILE STATUS ERROR]",
        error
      );

      if (!cancelled) {
        setPublicProfileError(
          error?.message ||
            "Could not load public profile setting."
        );
      }
    } finally {
      if (!cancelled) {
        setPublicProfileLoading(false);
      }
    }
  }

  loadPublicProfileSetting();

  return () => {
    cancelled = true;
  };
}, [open]);

        const handleTogglePublicProfile = async () => {
  if (
    publicProfileSaving ||
    publicProfileLoading
  ) {
    return;
  }

  const nextValue = !publicProfileEnabled;

  try {
    setPublicProfileSaving(true);
    setPublicProfileError("");

    const token = localStorage.getItem("token");

    if (!token) {
      throw new Error(
        "Missing authentication token."
      );
    }

    const res = await fetch(
      `${API_BASE}/api/users/me/public-profile`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-TG-INITDATA":
            window.Telegram?.WebApp?.initData || "",
        },
        body: JSON.stringify({
          publicProfile: nextValue,
        }),
      }
    );

    const data = await res
      .json()
      .catch(() => ({}));

    if (!res.ok) {
      throw new Error(
        data?.error ||
          "Could not update public profile."
      );
    }

    const savedValue =
      data?.publicProfile === true;

    setPublicProfileEnabled(savedValue);

    setUser?.((previousUser) => ({
      ...(previousUser || {}),
      public_profile: savedValue,
    }));
  } catch (error) {
    console.error(
      "[PUBLIC PROFILE UPDATE ERROR]",
      error
    );

    setPublicProfileError(
      error?.message ||
        "Could not update public profile."
    );
  } finally {
    setPublicProfileSaving(false);
  }
};

  if (!open) return null;


const shortTonWalletAddress = tonWalletAddress
  ? `${tonWalletAddress.slice(0, 6)}...${tonWalletAddress.slice(-6)}`
  : "";

const handleTonWalletClick = async () => {
  try {
    if (tonWallet) {
      await tonConnectUI.disconnect();
      return;
    }

    await tonConnectUI.openModal();
  } catch (error) {
    console.error("TON wallet action failed:", error);
  }
};

const copyTonWalletAddress = async () => {
  if (!tonWalletAddress) return;

  try {
    await navigator.clipboard.writeText(
      tonWalletAddress
    );

    setWalletCopied(true);

    setTimeout(() => {
      setWalletCopied(false);
    }, 1500);
  } catch (error) {
    console.error(
      "Could not copy TON wallet address:",
      error
    );
  }
};



  return (
  <>
    <div className="modal_box profile_modal">
      <div className="modal_title">
        Your Profile

        <button type="button" className="modal_close" onClick={onClose} />
      </div>

      <motion.div className="modal_body"
      initial={{ opacity: 0 }}
animate={{ opacity: 1 }}
exit={{ opacity: 0 }}>


      
        <div
          className="modal_inside_2"
          style={{
            padding: 10,
            boxSizing: "border-box",
          }}
        >
          {/* AVATAR / NAME BLOCK */}
<div
  style={{
    width: "100%",
    height: 56,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 6,
    padding: 5,
    borderRadius: 8,
    backgroundColor: "#6a4628",
    border: "2px solid #401f0e",
    boxSizing: "border-box",
    overflow: "hidden",

    ...profileBannerStyle,
  }}
>
  {/* USER AVATAR */}
  <div
    style={{
      flex: "0 0 42px",
      width: 42,
      height: 42,
      background: "#000",
      borderRadius: 8,
      overflow: "hidden",
    }}
  >
    <img
      src={avatar}
      alt=""
      draggable={false}
      onError={(e) => {
        e.currentTarget.onerror = null;
        e.currentTarget.src = svgFallback(name);
      }}
      style={{
        display: "block",
        width: "100%",
        height: "100%",
        objectFit: "cover",
        borderRadius: 8,
      }}
    />
  </div>

{/* USER EMOJI */}
{emojiSrc && (
  <div
    style={{
      flex: "0 0 42px",
      width: 42,
      height: 42,
      background: "transparent",
      borderRadius: 6,
      overflow: "hidden",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <img
      src={emojiSrc}
      alt=""
      draggable={false}
      onError={(e) => {
        e.currentTarget.style.display = "none";
      }}
      style={{
        display: "block",
        width: 34,
        height: 34,
        objectFit: "contain",
        borderRadius: 6,
      }}
    />
  </div>
)}
  {/* NAME AND SIDE */}
  <div
    style={{
      flex: "1 1 0",
      minWidth: 0,
      overflow: "hidden",
    }}
  >
   <div
  title={name}
  style={{
    minWidth: 0,
    textAlign: "left",
    fontSize: 16,
    fontWeight: 600,
    lineHeight: "16px",
    overflow: "hidden",
  }}
>
  <span
    style={{
      display: "inline-block",
      minWidth: 0,
      maxWidth: "100%",
      color: "#fff",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",

      ...getNameStyleCSS(
        user?.active_name_style ??
        user?.activeNameStyle ??
        ""
      ),
    }}
  >
    {name}
  </span>
</div>

    <div
      className="shadow1"
      title={arenaSideLabel}
      style={{
        textAlign: "left",
        fontSize: 13,
        fontWeight: 600,
        color: "#f2d94b",
        lineHeight: "18px",
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
      }}
    >
      {arenaSideLabel}
    </div>
  </div>

  {/* EDIT COSMETICS */}
  <button
    type="button"
    className="game-button-small yellow-button"
    onClick={() => setShowNameCosmeticsModal(true)}
    style={{
      flex: "0 0 42px",
      width: 42,
      height: 42,
      minWidth: 42,
      padding: 0,
      fontSize: 20,
      lineHeight: "42px",
      marginLeft: 2,
    }}
  >
    <img
      src="/v2/edit.png"
      alt=""
      style={{
        height: 24,
        objectFit: "contain",
      }}
    />
    </button>
</div>

{/* USER PASSES */}
<div
  style={{
    width: "100%",
    marginTop: 8,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
    boxSizing: "border-box",
  }}
>
  {/* GAME PASS */}
  <button
    type="button"
    onClick={() => openPassPage("gamepass")}
    style={{
      width: "100%",
      minWidth: 0,
      minHeight: 52,
      padding: "6px 8px",
      borderRadius: 8,
      border: hasGamePass
        ? "2px solid #77c943"
        : "2px solid #74332a",
      background: hasGamePass
        ? "linear-gradient(180deg, #568d2f 0%, #31581c 100%)"
        : "linear-gradient(180deg, #75402e 0%, #4b271d 100%)",
      boxShadow:
        "inset 0 1px 0 rgba(255,255,255,0.18), 0 3px 0 rgba(42,16,7,0.7)",
      display: "flex",
      alignItems: "center",
      gap: 7,
      cursor: "pointer",
      boxSizing: "border-box",
    }}
  >
    <img
      src="/pass1.png"
      alt=""
      style={{
        width: 34,
        height: 34,
        flexShrink: 0,
        objectFit: "contain",
      }}
    />

    <div
      style={{
        minWidth: 0,
        flex: 1,
        textAlign: "left",
      }}
    >
      <div
        className="shadow1"
        style={{
          color: "#fff",
          fontSize: 12,
          lineHeight: "16px",
          fontWeight: 700,
          whiteSpace: "nowrap",
        }}
      >
        OG PASS
      </div>

      <div
        className="shadow1"
        style={{
          marginTop: 2,
          color: hasGamePass
            ? "#c8ff91"
            : "#ff9b8e",
          fontSize: 11,
          lineHeight: "13px",
          fontWeight: 700,
        }}
      >
        {hasGamePass ? "ACTIVE" : "INACTIVE"}
      </div>
    </div>

   <img
  src="/v2/drop.png"
  alt=""
  draggable={false}
  style={{
    width: 12,
    height: 8,
    flexShrink: 0,
    objectFit: "contain",
    transform: "rotate(-90deg)",
  }}
/>
  </button>

  {/* BATTLE PASS */}
  <button
    type="button"
    onClick={() => openPassPage("battlePass")}
    style={{
      width: "100%",
      minWidth: 0,
      minHeight: 52,
      padding: "6px 8px",
      borderRadius: 8,
      border: hasBattlePass
        ? "2px solid #77c943"
        : "2px solid #74332a",
      background: hasBattlePass
        ? "linear-gradient(180deg, #568d2f 0%, #31581c 100%)"
        : "linear-gradient(180deg, #75402e 0%, #4b271d 100%)",
      boxShadow:
        "inset 0 1px 0 rgba(255,255,255,0.18), 0 3px 0 rgba(42,16,7,0.7)",
      display: "flex",
      alignItems: "center",
      gap: 7,
      cursor: "pointer",
      boxSizing: "border-box",
    }}
  >
    <img
      src="/pass2.png"
      alt=""
      style={{
        width: 34,
        height: 34,
        flexShrink: 0,
        objectFit: "contain",
      }}
    />

    <div
      style={{
        minWidth: 0,
        flex: 1,
        textAlign: "left",
      }}
    >
      <div
        className="shadow1"
        style={{
          color: "#fff",
          fontSize: 12,
          lineHeight: "16px",
          fontWeight: 700,
          whiteSpace: "nowrap",
        }}
      >
        OG PASS PLUS
      </div>

      <div
        className="shadow1"
        style={{
          marginTop: 2,
          color: hasBattlePass
            ? "#c8ff91"
            : "#ff9b8e",
          fontSize: 11,
          lineHeight: "13px",
          fontWeight: 700,
        }}
      >
        {battlePassLoading
  ? "LOADING"
  : hasBattlePass
    ? "ACTIVE"
    : "INACTIVE"}
      </div>
    </div>

    <img
  src="/v2/drop.png"
  alt=""
  draggable={false}
  style={{
    width: 12,
    height: 8,
    flexShrink: 0,
    objectFit: "contain",
    transform: "rotate(-90deg)",
  }}
/>
  </button>
</div>

{/* LEVEL */}
<div
  style={{
    width: "100%",
    marginTop: 12,
    boxSizing: "border-box",
  }}
>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 0,
      marginBottom: 8,
      minWidth: 0,
    }}
  >
    <div
      className="shadow1"
      style={{
        width: 42,
        height: 42,
        flex: "0 0 42px",
        borderRadius: "50%",
        background: "#8c441f",
        border: "3px solid #ffe16a",
        color: "#fff",
        fontSize: 20,
        lineHeight: "36px",
        fontWeight: 900,
        textAlign: "center",
        zIndex: 2,
        boxSizing: "border-box",
      }}
    >
      {level}
    </div>

    <div
      style={{
        flex: 1,
        minWidth: 0,
        height: 30,
        marginLeft: -4,
        background: "#0b0705",
        border: "3px solid #5d2a13",
        borderRadius: 5,
        overflow: "hidden",
        position: "relative",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          height: "100%",
          width: `${levelPercent}%`,
          background:
            "linear-gradient(180deg, #b6ffff 0%, #59ccff 55%, #428bff 100%)",
        }}
      />

      <div
        className="shadow1"
        style={{
          position: "absolute",
          inset: 0,
          color: "#fff",
          fontSize: 16,
          lineHeight: "24px",
          textAlign: "center",
          fontWeight: 600,
        }}
      >
        {level}/30
      </div>
    </div>
  </div>

  <button
  type="button"
  className="game-button yellow-button"
  onClick={onIncreaseLevel}
  style={{
    width: "100%",
    fontSize: 17,
    whiteSpace: "nowrap",
    marginBottom: 6,
  }}
>
  Increase Level
</button>


<button
  type="button"
  onClick={() => {
    onClose?.();

    setTimeout(() => {
      onNavigate?.("addHomeScreen");
    }, 0);
  }}
  style={{
    width: "100%",
    minHeight: 54,

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    gap: 10,

    marginTop: 2,
    marginBottom: 5,

    padding: "7px 7px",

    boxSizing: "border-box",

    border: "2px solid #5d2a13",
    borderRadius: 8,

    background:
      "linear-gradient(180deg, #6db54a 0%, #438b2c 58%, #337522 100%)",

    boxShadow:
      "inset 0 2px 0 rgba(255,255,255,0.22), 0 4px 0 #5d2a13",

    cursor: "pointer",

    color: "#fff",

    fontFamily: '"Fredoka", sans-serif',

    appearance: "none",
    WebkitAppearance: "none"
  }}
>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 6,

      minWidth: 0,

      textAlign: "left",
    }}
  >
    <div
      style={{
        width: 38,
        height: 38,

        flex: "0 0 38px",

        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        borderRadius: 7,

        background:
          "rgba(43, 91, 25, 0.75)",

      }}
    >
      <img
        src="/icon.png"
        alt=""
        style={{
          width: 30,
          height: 30,
          objectFit: "contain",
          borderRadius: 4,
          display: "block",
        }}
        onError={(e) => {
          e.currentTarget.style.display =
            "none";
        }}
      />
    </div>


    <div
      style={{
        minWidth: 0,
      }}
    >
      <div
        className="shadow1"
        style={{
          color: "#fff",

          fontSize: 14,
          lineHeight: "17px",

          fontWeight: 600,

          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        ADD TAPTOPIA TO HOME SCREEN
      </div>

      <div
        className="shadow1"
        style={{
          marginTop: 2,

          color: "#dfff80",

          fontSize: 11,
          lineHeight: "13px",

          fontWeight: 700,
        }}
      >
        Quick access + earn 25 Runes
      </div>
    </div>
  </div>


  <div
    className="shadow1"
    style={{
      flex: "0 0 auto",

      color: "#ffe96c",

      fontSize: 22,
      lineHeight: "22px",

      fontWeight: 900,

      marginLeft: 4,
    }}
  >
    ›
  </div>
</button>

</div>

{/* PROFILE STATS */}
<div
  style={{
    width: "100%",
    marginTop: 8,
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 8,
    alignItems: "stretch",
    boxSizing: "border-box",
    overflow: "hidden",
  }}
>
  <ProfileStatBox
    icon="/v2/winrate.png"
    label="Wins"
    value={profileWins}
    fallbackIcon="/s_attack.png"
  />

  <ProfileStatBox
    icon="/v2/trophy.png"
    label="Trophy"
    value={trophy}
    fallbackIcon="/v2/trophy.png"
  />

  <ProfileStatBox
    icon="/ticket.png"
    label="Tickets"
    value={user?.tickets ?? 0}
  />

  <ProfileStatBox
    icon="/sparks.png"
    label="Sparks"
    value={user?.sparks ?? 0}
    onClick={() => onOpenShop?.("coin_1")}
    title="Purchase Sparks"
  />

  <ProfileStatBox
    icon="/runes.png"
    label="Runes"
    value={user?.runes ?? 0}
    onClick={() => onOpenShop?.("coin_2")}
    title="Purchase Runes"
  />

  <ProfileStatBox
    icon="/icon_tapi.png"
    label="$Tapi"
    value={user?.tapi ?? 0}
    onClick={() => setShowMyTapisModal(true)}
    title="Open Airdrop"
  />
</div>

          {/* PUBLIC PROFILE */}
<div
  style={{
    width: "100%",
    marginTop: 10,
    padding: 9,
    boxSizing: "border-box",
    borderRadius: 8,
    border: "2px solid #5d2a13",
    background: "rgba(70, 30, 14, 0.72)",
    boxShadow:
      "inset 0 1px 0 rgba(255,255,255,0.15), 0 3px 0 rgba(0,0,0,0.24)",
  }}
>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      minWidth: 0,
    }}
  >
    <div
  style={{
    width: 48,
    height: 48,
    flex: "0 0 48px",
    borderRadius: "50%",
    background: "rgba(0,0,0,0.22)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow:
      "inset 0 1px 0 rgba(255,255,255,0.2)",
  }}
>
  <div
    style={{
      width: 30,
      height: 30,
      flex: "0 0 30px",
      borderRadius: "50%",
      background: "#5da2eb",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx="12"
        cy="8"
        r="4"
        stroke="#fff"
        strokeWidth="2.2"
      />

      <path
        d="M5 19C5.8 15.7 8.2 14 12 14C15.8 14 18.2 15.7 19 19"
        stroke="#fff"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  </div>
</div>

    <div
      style={{
        flex: 1,
        minWidth: 0,
        textAlign: "left",
      }}
    >
      <div
        className="shadow1"
        style={{
          color: "#fff",
          fontSize: 16,
          lineHeight: "19px",
          fontWeight: 600,
        }}
      >
        Public Profile
      </div>

      <div
        className="shadow1"
        style={{
          marginTop: 2,
          color: "rgba(255,255,255,0.75)",
          fontSize: 12,
          lineHeight: "15px",
          fontWeight: 600,
        }}
      >
        {publicProfileLoading
  ? "Loading privacy setting..."
  : publicProfileEnabled
    ? "Your profile is visible to other players."
    : "Your profile is hidden from other players."}
      </div>
    </div>

    <button
  type="button"
  role="switch"
  aria-checked={publicProfileEnabled}
  aria-label="Toggle public profile"
  disabled={
    publicProfileSaving ||
    publicProfileLoading
  }
  onClick={handleTogglePublicProfile}
  style={{
    width: 54,
    minWidth: 54,
    height: 30,
    padding: 0,
    margin: 0,
    border: "2px solid #5d2a13",
    borderRadius: 999,
    background:
      publicProfileEnabled
        ? "linear-gradient(180deg, #62b93e 0%, #367b23 100%)"
        : "linear-gradient(180deg, #6a321f 0%, #402014 100%)",
    boxShadow:
      "inset 0 1px 0 rgba(255,255,255,0.18), 0 2px 0 rgba(0,0,0,0.28)",
    position: "relative",
    cursor:
      publicProfileSaving ||
      publicProfileLoading
        ? "default"
        : "pointer",
    opacity:
      publicProfileSaving ||
      publicProfileLoading
        ? 0.55
        : 1,
    transition:
      "background 0.2s ease, opacity 0.2s ease",
    appearance: "none",
    WebkitAppearance: "none",
    boxSizing: "border-box",
    flex: "0 0 54px",
  }}
>
  <span
    style={{
      position: "absolute",
      top: 3,
      left: publicProfileEnabled ? 27 : 3,
      width: 20,
      height: 20,
      borderRadius: "50%",
      background:
        publicProfileSaving ||
        publicProfileLoading
          ? "#c7c7c7"
          : "#fff",
      border: "1px solid rgba(0,0,0,0.35)",
      boxShadow:
        "0 1px 2px rgba(0,0,0,0.45)",
      transition: "left 0.2s ease",
      boxSizing: "border-box",
      pointerEvents: "none",
    }}
  />
</button>
  </div>
</div>

{publicProfileError && (
  <div
    className="shadow1"
    style={{
      marginTop: 6,
      color: "#ff8178",
      fontSize: 12,
      lineHeight: "15px",
      fontWeight: 700,
      textAlign: "center",
    }}
  >
    {publicProfileError}
  </div>
)}

          <div
  style={{
    width: "100%",
    marginTop: 10,
    padding: 9,
    boxSizing: "border-box",
    borderRadius: 8,
    border: tonWallet
      ? "2px solid #4399ea"
      : "2px solid #5d2a13",
    background: tonWallet
      ? "linear-gradient(135deg, rgba(22, 83, 145, 0.88) 0%, rgba(18, 52, 93, 0.94) 100%)"
      : "rgba(70, 30, 14, 0.72)",
    boxShadow:
      "inset 0 1px 0 rgba(255,255,255,0.15), 0 3px 0 rgba(0,0,0,0.24)",
  }}
>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      minWidth: 0,
    }}
  >
    <div
      style={{
        width: 48,
        height: 48,
        flex: "0 0 48px",
        borderRadius: "50%",
        background: tonWallet
          ? "#5da2eb"
          : "rgba(0,0,0,0.22)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow:
          "inset 0 1px 0 rgba(255,255,255,0.2)",
      }}
    >
      <img
        src="/ton.svg"
        alt="TON"
        draggable={false}
        style={{
          width: 30,
          height: 30,
          objectFit: "contain",
        }}
      />
    </div>

    <div
      style={{
        flex: 1,
        minWidth: 0,
        textAlign: "left",
      }}
    >
      <div
        className="shadow1"
        style={{
          color: "#fff",
          fontSize: 16,
          lineHeight: "19px",
          fontWeight: 600,
        }}
      >
        TON Wallet
      </div>

      <div
        className="shadow1"
        title={tonWalletAddress}
        style={{
          marginTop: 2,
          color: tonWallet
            ? "#a8d7ff"
            : "rgba(255,255,255,0.75)",
          fontSize: 13,
          lineHeight: "17px",
          fontWeight: 600,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {tonWallet
          ? shortTonWalletAddress
          : "No wallet connected"}
      </div>
    </div>

    <div
      className="shadow1"
      style={{
        flex: "0 0 auto",
        padding: "4px 7px",
        borderRadius: 5,
        background: tonWallet
          ? "rgba(52, 190, 99, 0.88)"
          : "rgba(0,0,0,0.2)",
        color: "#fff",
        fontSize: 10,
        lineHeight: "14px",
        fontWeight: 700,
      }}
    >
      {walletSaving
  ? "SAVING"
  : tonWallet
    ? "CONNECTED"
    : "OFFLINE"}

    </div>
  </div>

  <div
    style={{
      width: "100%",
      marginTop: 9,
      display: "grid",
      gridTemplateColumns: tonWallet
        ? "1fr 1fr"
        : "1fr",
      gap: 8,
    }}
  >
    {tonWallet && (
      <button
        type="button"
        className="game-button blue-button"
        onClick={copyTonWalletAddress}
        style={{
          width: "100%",
          fontSize: 13,
        }}
      >
        {walletCopied ? "COPIED!" : "COPY ADDRESS"}
      </button>
    )}

    <button
      type="button"
      className={
        tonWallet
          ? "game-button red-button"
          : "game-button blue-button"
      }
      onClick={handleTonWalletClick}
      style={{
        width: "100%",
        fontSize: 13,
      }}
    >
      {tonWallet
        ? "DISCONNECT"
        : "CONNECT WALLET"}
    </button>
  </div>
</div>

{walletSaveError && (
  <div
    className="shadow1"
    style={{
      marginTop: 8,
      color: "#ff8178",
      fontSize: 12,
      lineHeight: "15px",
      fontWeight: 700,
      textAlign: "center",
    }}
  >
    {walletSaveError}
  </div>
)}


                      {/* EMAIL */}
{/* EMAIL */}
<div
  style={{
    width: "100%",
    marginTop: 10,
    padding: 9,
    boxSizing: "border-box",
    borderRadius: 8,
    border: "2px solid #5d2a13",
    background: "rgba(70, 30, 14, 0.72)",
    boxShadow:
      "inset 0 1px 0 rgba(255,255,255,0.15), 0 3px 0 rgba(0,0,0,0.24)",
  }}
>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      minWidth: 0,
    }}
  >
    {/* MESMO CÍRCULO EXTERNO DO TON */}
    <div
      style={{
        width: 48,
        height: 48,
        flex: "0 0 48px",
        borderRadius: "50%",
        background: "rgba(0,0,0,0.22)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow:
          "inset 0 1px 0 rgba(255,255,255,0.2)",
      }}
    >
      {/* ÍCONE INTERNO 30x30, IGUAL AO TON */}
      <div
        style={{
          width: 30,
          height: 30,
          flex: "0 0 30px",
          borderRadius: "50%",
          background: "#5da2eb",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg
          width="19"
          height="19"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="3"
            y="5"
            width="18"
            height="14"
            rx="1.5"
            stroke="#fff"
            strokeWidth="2.4"
          />

          <path
            d="M4 7L12 13L20 7"
            stroke="#fff"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>

    {/* TEXTO */}
    <div
      style={{
        flex: 1,
        minWidth: 0,
        textAlign: "left",
      }}
    >
      <div
        className="shadow1"
        style={{
          color: "#fff",
          fontSize: 16,
          lineHeight: "19px",
          fontWeight: 600,
        }}
      >
        Email
      </div>

      <div
        className="shadow1"
        title={emailStatus.email || ""}
        style={{
          marginTop: 2,
          color: emailStatus.verified
            ? "#8ee878"
            : "rgba(255,255,255,0.75)",
          fontSize: 13,
          lineHeight: "17px",
          fontWeight: 600,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {emailStatus.loading
  ? "Loading..."
  : emailStatus.email?.toLowerCase().endsWith("@telegram.local")
    ? "No verified email"
    : emailStatus.verified
      ? emailStatus.email
      : "No verified email"}
      </div>
    </div>

    {/* STATUS À DIREITA, MESMO PADRÃO DO OFFLINE/CONNECTED */}
    <div
      className="shadow1"
      style={{
        flex: "0 0 auto",
        padding: "4px 7px",
        borderRadius: 5,
        background: emailStatus.verified
          ? "rgba(52, 190, 99, 0.88)"
          : "rgba(0,0,0,0.2)",
        color: "#fff",
        fontSize: 10,
        lineHeight: "14px",
        fontWeight: 700,
      }}
    >
      {emailStatus.loading
        ? "LOADING"
        : emailStatus.verified
          ? "VERIFIED"
          : "UNVERIFIED"}
    </div>
  </div>

  {/* REWARD - SOMENTE SE AINDA NÃO VERIFICOU */}
  {!emailStatus.verified &&
    !emailStatus.rewardAlreadyPaid &&
    Number(emailStatus?.reward?.amount || 0) > 0 && (
      <div
        style={{
          marginTop: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 5,
        }}
      >
        <div
          className="shadow1"
          style={{
            color: "rgba(255,255,255,0.82)",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          Verify and earn
        </div>

        <img
          src={
            emailStatus?.reward?.currency === "runes"
              ? "/runes.png"
              : "/sparks.png"
          }
          alt=""
          style={{
            width: 20,
            height: 20,
            objectFit: "contain",
          }}
        />

        <div
          className="shadow1"
          style={{
            color: "#ffe16a",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {emailStatus.reward.amount}
        </div>
      </div>
    )}

  {/* MESMO ESPAÇAMENTO DO BOTÃO DO TON */}
  <div
    style={{
      width: "100%",
      marginTop: 9,
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: 8,
    }}
  >
    <button
      type="button"
      className={
        emailStatus.verified
          ? "game-button green-button"
          : "game-button blue-button"
      }
      onClick={() => {
        if (!emailStatus.loading) {
          setShowEmailModal(true);
        }
      }}
      disabled={emailStatus.loading}
      style={{
        width: "100%",
        fontSize: 13,
      }}
    >
      {emailStatus.loading
        ? "LOADING..."
        : emailStatus.verified
          ? "VERIFIED"
          : "VERIFY EMAIL"}
    </button>
  </div>
</div>



           {/* SIDE */}
          <div
  onClick={openChangeSideModal}
  role="button"
  title="Change Side"
  style={{
    width: "100%",
    marginTop: 12,
    background: "rgba(70, 30, 14, 0.72)",
    border: "3px solid #5d2a13",
    borderRadius: 7,
    padding: "5px 5px",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    gap: 10,
    overflow: "hidden",
    cursor: "pointer",
  }}
>
            <img
              src="/v2/logo.png"
              alt=""
              style={{
                width: 50,
                flex: "0 0 50px",
                objectFit: "contain",
              }}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />

            <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
              <div
                className="shadow1"
                style={{
                  color: "#fff",
                  fontSize: 15,
                  lineHeight: "19px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                You are currently on the:
              </div>

              <div
                className="shadow1"
                style={{
                  color: isMoon ? "#ff6845" : "#ffe16a",
                  fontSize: 18,
                  lineHeight: "21px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {sideLabel}
              </div>
            </div>

            <img
              src={sideIcon}
              alt=""
              style={{
                width: 42,
                height: 42,
                flex: "0 0 42px",
                objectFit: "contain",
              }}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />

            <img
              src="/v2/drop.png"
              alt=""
              style={{ marginLeft: -5, height: 10 }}
            />
          </div>

          {/* DEFENSE DECK */}
          <div style={{ marginTop: 12, width: "100%", overflow: "hidden" }}>
            <div
              style={{
                width: "100%",
                height: 42,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                padding: "0 10px",
                paddingRight: 4,
                boxSizing: "border-box",
                background: "#87401f",
                border: "3px solid #5d2a13",
                borderRadius: "8px 8px 0 0",
                overflow: "hidden",
              }}
            >
              <div
                className="shadow1"
                style={{
                  color: "#fff",
                  fontSize: 16,
                  lineHeight: "28px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Defense Deck ({deckCardIds.length}/{deckSize})
              </div>

              <button
                type="button"
                onClick={onOpenDefenseDeck}
                className="game-button-small yellow-button"
              >
                EDIT
              </button>
            </div>

            <div
              style={{
                width: "100%",
                background: "rgba(80, 50, 25, 0.58)",
                borderLeft: "3px solid #5d2a13",
                borderRight: "3px solid #5d2a13",
                borderBottom: "3px solid #5d2a13",
                borderRadius: "0 0 8px 8px",
                padding: 8,
                boxSizing: "border-box",
                overflow: "hidden",
              }}
            >
             {deckLoading ? (
  <div
    style={{
      height: 132,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <LoadingSpinner text="Loading Deck" size={48} />
  </div>
) : defensePreviewCards.length === 0 ? (
  <div
    style={{
      minHeight: 92,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      padding: "10px 12px",
      boxSizing: "border-box",
    }}
  >
    <div
      className="shadow2"
      style={{
        color: "#fff",
        fontSize: 18,
        fontWeight: 600,
        marginBottom: 3,
      }}
    >
      No Defense Deck Set
    </div>

    <div
      className="shadow1"
      style={{
        marginTop: 5,
        color: "#ccc",
        fontWeight: 600,
        fontSize: 14,
        lineHeight: "16px",
      }}
    >
      Choose up to {deckSize} fighters to defend you while you're away.
    </div>
  </div>
) : (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: 8,
      overflow: "hidden",
    }}
  >
    {Array.from({ length: PREVIEW_LIMIT }).map((_, idx) => (
      <div
        key={
          defensePreviewCards[idx]?.id ||
          `profile-defense-empty-${idx}`
        }
        style={{
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <MiniCard
          card={defensePreviewCards[idx]}
          emptyLabel="EMPTY"
        />
      </div>
    ))}
  </div>
)}
            </div>
          </div>

         
        </div>

        <div className="modal_footer">
          <button
  type="button"
  className="game-button green-button"
  onClick={() => setShowInviteFriendsModal(true)}
>
  INVITE FRIENDS AND EARN
</button>

        </div>
      </motion.div>
        </div>

    {showSideModal && sideTarget && (
        <div
          className="modal_box"
        >
          <div className="modal_title">
            Switch Your Side
            <button
              type="button"
              className="modal_close"
              onClick={closeChangeSideModal}
            />
          </div>

          <motion.div className="modal_body"           initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.92, opacity: 0 }}>
            <div
              className="modal_inside"
            >
              <div
                className="shadow2"
                style={{
                  fontSize: 24,
                  marginBottom: 10,
                  fontWeight: 600,
                }}
              >
                Switch To The {sideTarget === "sun" ? "Sun" : "Moon"} Side?
              </div>

              <img
                src={sideTarget === "sun" ? "/v2/side_good.png" : "/v2/side_bad.png"}
                alt=""
                onError={(e) => {
                  e.currentTarget.src =
                    sideTarget === "sun" ? "/sun_tap.png" : "/moon_tap.png";
                }}
                style={{
                  height: 120,
                  maxWidth: "100%",
                  objectFit: "contain",
                  marginBottom: 0,
                }}
              />

              <div
              className="info"
                style={{
                }}
              >
                <div
                  className="shadow1"
                  style={{
                    color: "#fff",
                    fontSize: 18,
                    lineHeight: "24px",
                    fontWeight: 800,
                  }}
                >
                  Cost:
                  <img
                    src={sideUseBypass ? "/runes.png" : "/sparks.png"}
                    alt=""
                    style={{
                      width: 22,
                      height: 22,
                      objectFit: "contain",
                      verticalAlign: "middle",
                      margin: "0 4px",
                    }}
                  />
                  <span style={{ color: "#ffeb4f" }}>
                    {sideUseBypass
                      ? SIDE_CHANGE_BYPASS_RUNES_PRICE
                      : changeSidePrice}{" "}
                    {sideUseBypass ? "Runes" : "Sparks"}
                  </span>
                </div>

                <div
                  className="shadow1"
                  style={{
                    color: "#fff",
                    fontSize: 13,
                    lineHeight: "17px",
                    fontWeight: 700,
                    marginTop: 5,
                  }}
                >
                  The {currentSide === "sun" ? "Sun" : "Moon"} Side Will Remember
                  This Betrayal
                </div>
              </div>

              {isSideCooldownActive && (
                <div
                className="info"
                  style={{
                    marginTop: 20
                  }}
                >
                  <div
                    className="shadow1"
                    style={{
                      color: "#ffeb4f",
                      fontSize: 15,
                      lineHeight: "19px",
                      fontWeight: 800,
                    }}
                  >
                    Cooldown Active: {formatSideCooldown(sideCooldownRemainingMs)}
                  </div>

                  <button
                    type="button"
                    className={`game-button-small ${
                      sideUseBypass ? "green-button" : "yellow-button"
                    }`}
                    onClick={() => setSideUseBypass((v) => !v)}
                    style={{
                      marginTop: 6,
                      fontSize: 14,
                    }}
                  >
                    {sideUseBypass
                      ? "Using Runes Bypass"
                      : `Use ${SIDE_CHANGE_BYPASS_RUNES_PRICE} Runes Now`}
                  </button>
                </div>
              )}

              <div
                className="shadow1"
                style={{
                  float: "left",
                  width: "100%",
                  color: "#ffeb4f",
                  fontSize: 16,
                  fontWeight: 900,
                  marginTop: 30,
                }}
              >
                IMPORTANT
              </div>

              <div
                className="shadow1"
                style={{
                  color: "#fff",
                  fontSize: 16,
                  lineHeight: "15px",
                  fontWeight: 600,
                  marginTop: 5,
                }}
              >
                When you switch sides, only your side changes. Previously purchased cards remain on the side they were earned and are not moved.
              </div>

              {sideErrorMessage && (
                <div
                  className="shadow1"
                  style={{
                    color: "#ff6b55",
                    fontSize: 16,
                    lineHeight: "18px",
                    fontWeight: 600,
                    marginTop: 10,
                  }}
                >
                  {sideErrorMessage}
                </div>
              )}
</div>

              <div className="modal_footer">
                <button
                  type="button"
                  className="game-button green-button"
                  disabled={
                    sideLoading ||
                    (!sideUseBypass && !canAffordSideChange) ||
                    (sideUseBypass && !canAffordSideBypass)
                  }
                  onClick={() => changeSide({ bypassCooldown: sideUseBypass })}
                  style={{
                    marginRight: 10,
                    opacity:
                      sideLoading ||
                      (!sideUseBypass && !canAffordSideChange) ||
                      (sideUseBypass && !canAffordSideBypass)
                        ? 0.55
                        : 1,
                  }}
                >
                  {sideLoading ? "Switching..." : "Confirm"}
                </button>

                <button
                  type="button"
                  className="game-button red-button"
                  onClick={closeChangeSideModal}
                >
                  CANCEL
                </button>
              
            </div>
          </motion.div>
        </div>
    )}

    {showSideSuccessModal && (
        <div
          className="modal_box"
        >
          <div className="modal_title">
            Success
            <button
              type="button"
              className="modal_close"
              onClick={() => setShowSideSuccessModal(false)}
            />
          </div>

          <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.92, opacity: 0 }}>
            <div
              className="modal_inside"
              style={{
                padding: 14,
                textAlign: "center",
                boxSizing: "border-box",
              }}
            >
              <div
                className="shadow2"
                style={{
                  color: "#fff",
                  fontSize: 22,
                  lineHeight: "27px",
                  fontWeight: 900,
                  marginBottom: 12,
                }}
              >
                Transaction Completed!
              </div>

              <div
                style={{
                  width: 78,
                  height: 78,
                  margin: "0 auto 12px",
                  borderRadius: 12,
                  background: "#2a1208",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={currentSide === "sun" ? "/v2/good.png" : "/v2/bad.png"}
                  alt=""
                  style={{
                    width: 52,
                    height: 52,
                    objectFit: "contain",
                  }}
                />
              </div>

              <div
                className="shadow1"
                style={{
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 0,
                }}
              >
                You Have Switched to the{" "}
                {currentSide === "sun" ? "Good" : "Bad"} Side! May Your New Allies Not Check Your Old Chat Logs
              </div>
            </div>
            <div className="modal_footer">
              <button
                type="button"
                className="game-button red-button"
                onClick={() => setShowSideSuccessModal(false)}
                style={{
                  width: 120,
                  fontSize: 16,
                }}
              >
                CLOSE
              </button>
              </div>
          </motion.div>
        </div>
    )}

                      <EmailVerificationModal
  open={showEmailModal}
  status={emailStatus}
  onClose={() =>
    setShowEmailModal(false)
  }
  onVerified={(data) => {
    setUser?.((previous) => ({
      ...(previous || {}),

      email:
        data?.user?.email ??
        data?.email ??
        previous?.email,

      email_verified_at:
        data?.user
          ?.email_verified_at ??
        data?.verifiedAt ??
        previous?.email_verified_at,

      email_verification_reward_paid_at:
        data?.user
          ?.email_verification_reward_paid_at ??
        previous
          ?.email_verification_reward_paid_at,

      sparks:
        data?.user?.sparks ??
        previous?.sparks,

      runes:
        data?.user?.runes ??
        previous?.runes,
    }));

    loadEmailStatus();
  }}
/>

<NameCosmeticsModal
  open={showNameCosmeticsModal}
  user={user}
  setUser={setUser}
  onClose={() => setShowNameCosmeticsModal(false)}
  onCloseProfile={onClose}
  onNavigate={onNavigate}
/>

 <MyTapisPage
  open={showMyTapisModal}
  user={user}
  setUser={setUser}
  onClose={() => setShowMyTapisModal(false)}
/>

<InviteFriendsModal
  open={showInviteFriendsModal}
  user={user}
  setUser={setUser}
  onClose={() => setShowInviteFriendsModal(false)}
  onCloseProfile={onClose}
  onNavigate={onNavigate}
/>

  </>
);
}